import React, { useEffect, useState, useMemo } from "react";
import {
  Row, Col, Card, CardBody, CardTitle, Button, Modal, ModalHeader, ModalBody, ModalFooter,
  Form, FormGroup, Label, Input, Spinner, Alert, UncontrolledTooltip
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { pb } from "../../helpers/pocketbase_helper";
import { RepositoryFactory, OrdenProduccionRepository, RelatedDataHelper } from "../../repositories";
import { createModuleLogger } from "../../utils/logger";

// Crear logger para este módulo
const ordenLogger = createModuleLogger('OrdenProduccion');
import TableContainer from "../../components/Common/DataTableContainer";
import Impresion from "./impresion";
import Planeacion from "./planeacion";
import CorteConversion from "./corte_conversion";
import PDFViewerModal from "../../components/Common/PDFViewerModal";
import IniciarOrdenButton from "../../components/IniciarOrdenButton";
import ReturnOrderButton from "../../components/ReturnOrderButton";
import ViewNotesButton from "../../components/ViewNotesButton";
import SolicitarMaterialButton from "../../components/SolicitarMaterialButton";
import MaterialStatusIndicator from "../../components/MaterialStatusIndicator";
import { handleCloneDirect } from "../../helpers/ordenCloneHelper";

// Utilidad para formato fecha/hora compatible con datetime-local y PocketBase
const getNow = () => {
  const now = new Date();
  const pad = n => n.toString().padStart(2, '0');
  const yyyy = now.getFullYear();
  const MM = pad(now.getMonth() + 1);
  const dd = pad(now.getDate());
  const hh = pad(now.getHours());
  const mm = pad(now.getMinutes());
  return `${yyyy}-${MM}-${dd}T${hh}:${mm}`;
};

// Utilidad para obtener el último día del mes actual
const getEndOfMonth = () => {
  const now = new Date();
  // Crear una fecha para el primer día del próximo mes y restar un día
  const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const pad = n => n.toString().padStart(2, '0');
  const yyyy = lastDay.getFullYear();
  const MM = pad(lastDay.getMonth() + 1);
  const dd = pad(lastDay.getDate());
  // Establecemos la hora al final del día (23:59)
  return `${yyyy}-${MM}-${dd}T23:59`;
};

// Convierte de datetime-local a formato ISO para PocketBase
const toPBDate = (val) => {
  if (!val) return "";
  if (val.includes("Z")) return val;
  return new Date(val).toISOString();
};

// Convierte de ISO a datetime-local para el input
const toInputDate = (val) => {
  if (!val) return "";
  const d = new Date(val);
  const pad = n => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

/**
 * Deep clones orden_produccion data for the clone feature
 * Copies all fields except id, numero, created, updated
 * Resets state fields and dates to current values
 * @param {Object} orden - The source order to clone
 * @returns {Object} Cloned order data ready for form population
 */
export const deepCloneOrdenData = (orden) => {
  if (!orden) return {};

  const cloned = {
    // Core fields
    nombre_producto: orden.nombre_producto,
    cantidad_solicitada: orden.cantidad_solicitada,
    oc: orden.oc,
    numero_reproceso: orden.numero_reproceso,
    tipo_trabajo: orden.tipo_trabajo,

    // Dates - reset to current
    fecha_inicio: getNow(),
    fecha_entrega: getNow(),
    corte_facturacion: getEndOfMonth(),

    // Booleans
    nuevo: orden.nuevo,
    reimpresion: orden.reimpresion,
    troquel_n: orden.troquel_n,
    troquel_e: orden.troquel_e,
    trabajo_interno: orden.trabajo_interno,

    // Technical fields
    cabida_preprensa: orden.cabida_preprensa,
    tintas: orden.tintas,
    detalles: orden.detalles,
    cantidad: orden.cantidad,
    sobrantes: orden.sobrantes,
    pliegos: orden.pliegos,
    acabado_general: orden.acabado_general,
    observaciones: orden.observaciones,

    // Relations (IDs can be copied directly)
    cliente: orden.cliente,
    asesor_comercial: orden.asesor_comercial,
    solicitud: orden.solicitud,
    producto: orden.producto,
    material: orden.material,
    medida: orden.medida,

    // State reset - cloned orders start in "editing" state
    activo: true,
    activa: null,
    finalizada: null,

    // Explicitly do NOT copy:
    // - id (will be auto-generated)
    // - numero (will be auto-generated by getNextSequenceNumber)
    // - created (will be set by database)
    // - updated (will be set by database)
  };

  return cloned;
};

const OrdenProduccion = () => {
  document.title = "Orden de Producción | GraphicForce - Printesco";

  // Inicializar repositorio de órdenes de producción
  const ordenRepository = useMemo(() => 
    RepositoryFactory.getRepository('orden_produccion'), 
  []);

  // Estados principales
  const [ordenes, setOrdenes] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [clientes, setClientes] = useState([]);
  const [asesoresComerciales, setAsesoresComerciales] = useState([]);
  const [materiales, setMateriales] = useState([]);
  const [medidas, setMedidas] = useState([]);
  const [recubrimientos, setRecubrimientos] = useState([]);
  const [recubrimientoTiposGlobal, setRecubrimientoTiposGlobal] = useState([]);
  const [recubrimientoSeleccion, setRecubrimientoSeleccion] = useState({});
  const [nuevoTipo, setNuevoTipo] = useState({});
  const [agregarExistente, setAgregarExistente] = useState({});
  const [parametrosBrilloInterno, setParametrosBrilloInterno] = useState([]);
  const [parametrosPlastificadoInterno, setParametrosPlastificadoInterno] = useState([]);
  const [parametrosPegueInterno, setParametrosPegueInterno] = useState([]);
  const [parametrosTroqueladoInterno, setParametrosTroqueladoInterno] = useState([]);
  const [parametrosAcabadoInterno, setParametrosAcabadoInterno] = useState([]);
  const [productos, setProductos] = useState([]);
  const [tiposSolicitud, setTiposSolicitud] = useState([]);
  const [tiposPinza, setTiposPinza] = useState([]);
  const [parametrosTinta, setParametrosTinta] = useState([]);
  const [modalAdd, setModalAdd] = useState(false);
  const [modalEdit, setModalEdit] = useState(false);

  const [form, setForm] = useState({
    numero: "",
    cliente: "",
    asesor_comercial: "",
    nombre_producto: "",
    fecha_inicio: getNow(),
    fecha_entrega: getNow(),
    corte_facturacion: getEndOfMonth(),
    facturacion: "",
    cantidad_solicitada: "",
    oc: "",
    numero_reproceso: "",
      trabajo_interno: false,
      reimpresion: false,
      tipo_producto: "",
      solicitud: "",
      referencia_trabajo: "",
    tipo_papel: "",
    tamano_papel: "",
    cabida_preprensa: "",
    tintas: "",
    detalles: "",
    cantidad: "",
    sobrantes: "",
    tamano_corte: "",
    troquel_n: false,
    troquel_e: false,
    pliegos: "",
    acabado_general: "",
    observaciones: "",
    // Campos de Troquelado - only keeping troquelado_interno
    troquelado_interno: [], // Array for subtipos de troquelado
    // Repuje has been completely removed
    // Estampado has been completely removed
    // Campos para impresion y corte_conversion
    impresion: [],
    corte_conversion: [],
  });
  const [editId, setEditId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [modalError, setModalError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [ordenToDelete, setOrdenToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Estados para el modal PDF
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false);
  const [pdfModalData, setPdfModalData] = useState({ ordenId: null, filename: '' });
  const [generatingPdf, setGeneratingPdf] = useState(false);

  const [plastificadoForm, setPlastificadoForm] = useState({
    plastificado_interno: [], // Array for subtipos de plastificado
  });
  
  const [troqueladoForm, setTroqueladoForm] = useState({
    troquelado_interno: [], // Array for subtipos de troquelado (only field kept)
  });

  // Repuje has been completely removed

  const [pegueForm, setPegueForm] = useState({
    pegue_interno: [], // Array for subtipos de pegue
  });

  const [brilloForm, setBrilloForm] = useState({
    brillo_interno: [], // Array de IDs de parametros_brillo_interno seleccionados
  });
  
  const [acabadoForm, setAcabadoForm] = useState({
    acabado_interno: [], // Array de IDs de parametros_acabado_interno seleccionados
  });

  // Estado para corte_conversion
  const [corteForm, setCorteForm] = useState([]);
  
  // Estado para impresion
  const [impresionForm, setImpresionForm] = useState([]);

  const [planeacionForm, setPlaneacionForm] = useState({
    planeacion_id: "",
    planeacion_abierto_ancho: "",
    planeacion_abierto_alto: "",
    planeacion_armado_ancho: "",
    planeacion_armado_alto: "",
    planeacion_armado_prof: "",
    planeacion_tintas: "",
    parametros_tinta: [], // Array of parametros_tinta IDs
    planeacion_lineatura: "",
    planeacion_tipo_pinza: "",
    planeacion_troquel_e: false,
    planeacion_troquel_n: false,
    planeacion_numero_troquel: "",
    planeacion_tamano: "",
    planeacion_cabida_preprensa: "",
  });

  // Estado de paginación controlada externamente

  // Estados para tracking de campos modificados (sistema isDirty)
  const [dirtyFields, setDirtyFields] = useState(new Set());
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false);
  const [pendingCloseAction, setPendingCloseAction] = useState(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  // Estado para alertas del sistema
  const [alert, setAlert] = useState({ show: false, type: '', title: '', message: '' });

  // Cargar datos con paginación
  useEffect(() => {
    async function fetchData() {
      try {
        // Fetch paginated data usando repositorio - obtener órdenes para edición
        const resultList = await ordenRepository.getOrdersForEditing(pageIndex + 1, pageSize);
        setOrdenes(resultList.items);
        setTotalRecords(resultList.totalItems);

                // Fetch other data usando RelatedDataHelper para optimización
        const formData = await RelatedDataHelper.getAllFormData();
        
        setClientes(formData.clientes);
        setAsesoresComerciales(formData.usuarios);
        setMateriales(formData.materiales);
        setProductos(formData.productos);
        setTiposSolicitud(formData.tiposSolicitud);
        setTiposPinza(formData.tiposPinza);
        setMedidas(formData.medidas);
        
        const recubrs = formData.recubrimientos;

        // Custom sort order: Brillo, Plastificado, Troquelado, Pegue/Pegado/Pegue en Maquina, Acabado
        const customOrder = {
          "BRILLO": 1,
          "PLASTIFICADO": 2,
          "TROQUELADO": 3,
          "PEGUE": 4,
          "PEGADO": 4,
          "PEGUE EN MAQUINA": 4,
          "ACABADO": 5
        };

        // Ordenar recubrimientos según orden personalizado

        recubrs.sort((a, b) => {
          const aOrder = customOrder[a.nombre.toUpperCase()] || 999;
          const bOrder = customOrder[b.nombre.toUpperCase()] || 999;
          return aOrder - bOrder || a.nombre.localeCompare(b.nombre);
        });

        // Verificar si ya existe un recubrimiento con nombre "ACABADO"
        const acabadoExiste = recubrs.some(r => r.nombre && r.nombre.toUpperCase() === "ACABADO");
        
        // Si no existe, agregar uno manualmente para la interfaz
        if (!acabadoExiste) {
          recubrs.push({
            id: "acabado_temp_id", // ID temporal
            nombre: "ACABADO",
            estado: true
          });
          
          // Re-ordenar después de agregar para asegurar que ACABADO aparezca después de PEGUE
          recubrs.sort((a, b) => {
            const aOrder = customOrder[a.nombre.toUpperCase()] || 999;
            const bOrder = customOrder[b.nombre.toUpperCase()] || 999;
            return aOrder - bOrder || a.nombre.localeCompare(b.nombre);
          });
        }

        setRecubrimientos(recubrs);
        ordenLogger.debug("Recubrimientos procesados", { count: recubrs.length });
        
        const tiposRecubrimiento = formData.tiposRecubrimiento;
        ordenLogger.debug("Tipos de recubrimiento cargados", { count: tiposRecubrimiento.length });
        setRecubrimientoTiposGlobal(tiposRecubrimiento);

                // Fetch parametros de proceso usando helper optimizado
        const parametrosProceso = await RelatedDataHelper.getAllParametrosProceso();
        
        setParametrosBrilloInterno(parametrosProceso.brillo);
        setParametrosPlastificadoInterno(parametrosProceso.plastificado);
        setParametrosPegueInterno(parametrosProceso.pegue);
        setParametrosTroqueladoInterno(parametrosProceso.troquelado);
        setParametrosAcabadoInterno(parametrosProceso.acabado);
        
        // Cargar parámetros de tinta
        const parametrosTintaRepository = RepositoryFactory.getRepository('parametros_tinta');
        const parametrosTintaData = await parametrosTintaRepository.getAllActive();
        setParametrosTinta(parametrosTintaData);
      } catch (err) {
        setModalError("Error al cargar datos.");
      }
    }
    fetchData();
  }, [pageIndex, pageSize]); // Re-fetch when pagination changes

  // Function to reload orders with current pagination
  const reloadOrdenes = async () => {
    try {
      const resultList = await ordenRepository.getOrdersForEditing(pageIndex + 1, pageSize);
      setOrdenes(resultList.items);
      setTotalRecords(resultList.totalItems);
    } catch (err) {
      setModalError("Error al recargar las órdenes.");
    }
  };

  // Función para mostrar alertas del sistema
  const showAlert = (type, title, message) => {
    setAlert({ show: true, type, title, message });
    
    // Hacer scroll hacia el mensaje después de un breve delay para que se renderice
    setTimeout(() => {
      const alertElement = document.getElementById('system-alert');
      if (alertElement) {
        alertElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'center' 
        });
      }
    }, 100);
    
    // Auto-ocultar después de 5 segundos
    setTimeout(() => {
      setAlert({ show: false, type: '', title: '', message: '' });
    }, 5000);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((modalAdd || modalEdit) && e.ctrlKey && (e.key === "g" || e.key === "G")) {
        e.preventDefault();
        if (modalAdd) {
          document.getElementById("orden-produccion-add-form")?.requestSubmit();
        }
        if (modalEdit) {
          document.getElementById("orden-produccion-edit-form")?.requestSubmit();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [modalAdd, modalEdit]);

  // Special effect to ensure medidas are loaded when modals open
  useEffect(() => {
    async function ensureMedidasLoaded() {
      if ((modalAdd || modalEdit) && medidas.length === 0) {
        try {
          ordenLogger.debug("Cargando medidas para modal");
          const medidasData = await RelatedDataHelper.getMedidas();
          ordenLogger.debug("Medidas cargadas", { count: medidasData.length });
          setMedidas(medidasData);
        } catch (err) {
          ordenLogger.error("Error cargando medidas para modal", err);
        }
      }
    }
    
    ensureMedidasLoaded();
  }, [modalAdd, modalEdit, medidas.length]);

  // useEffect para limpiar estados del sistema isDirty cuando se cierren modales
  useEffect(() => {
    if (!modalAdd && !modalEdit) {
      // Limpiar estados cuando se cierren ambos modales
      clearDirtyFields();
      setShowUnsavedChangesModal(false);
      setPendingCloseAction(null);
    }
  }, [modalAdd, modalEdit]);

  // useEffect para limpiar dirtyFields al abrir nuevos modales
  useEffect(() => {
    if (modalAdd || modalEdit) {
      clearDirtyFields();
    }
  }, [modalAdd, modalEdit]);

  // Maps para mostrar nombres en la tabla
  const clientesMap = useMemo(() => {
    const map = {};
    clientes.forEach(c => { map[c.id] = c.razon_social; });
    return map;
  }, [clientes]);
  const materialesMap = useMemo(() => {
    const map = {};
    materiales.forEach(m => {
      map[m.id] = m.texto_medida
        ? `${m.nombre} - ${m.texto_medida}`
        : m.nombre;
    });
    return map;
  }, [materiales]);
  const medidasMap = useMemo(() => {
    const map = {};
    medidas.forEach(m => { map[m.id] = m.nombre; });
    return map;
  }, [medidas]);

  // Tabla columnas
  const columns = useMemo(() => [
    {
      header: "N° Orden",
      accessorKey: "numero",
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        // Filtrar números - convertir a string para comparación
        const numeroValue = String(row.getValue(columnId) || "");
        const searchValue = String(filterValue || "");
        const matches = numeroValue.includes(searchValue);
        if (filterValue) {
          ordenLogger.debug("Filtro de número aplicado", { 
            filterValue: searchValue, 
            numeroValue, 
            matches 
          });
        }
        return matches;
      },
    },
    {
      header: "Cliente",
      accessorKey: "cliente",
      cell: info => clientesMap[info.getValue()] || "-",
      enableSorting: true,
      enableColumnFilter: true,
      filterFn: (row, columnId, filterValue) => {
        // Filtrar por nombre del cliente, no por ID
        const clienteNombre = clientesMap[row.getValue(columnId)] || "";
        const matches = clienteNombre.toLowerCase().includes(filterValue.toLowerCase());
        if (filterValue) {
          ordenLogger.debug("Filtro de cliente aplicado", { 
            filterValue, 
            clienteNombre, 
            matches 
          });
        }
        return matches;
      },
    },
    {
      header: "Producto",
      accessorKey: "nombre_producto",
      cell: info => info.getValue() || "-",
      enableSorting: true,
      enableColumnFilter: true,
    },
    {
      header: "Fecha Inicio",
      accessorKey: "fecha_inicio",
      cell: info => info.getValue() ? info.getValue().substring(0, 10) : "",
      enableSorting: true,
    },
    {
      header: "Acciones",
      accessorKey: "acciones",
      cell: info => {
        const orden = info.row.original;
        const rowId = `row-${orden.id || Math.random().toString(36).substring(7)}`;
        return (
          <div style={{ display: "flex", flexWrap: "nowrap", justifyContent: "center", gap: 2 }}>
            <Button id={`edit-${rowId}`} color="warning" size="sm" className="me-1 px-2 py-1" onClick={() => handleEdit(orden)}>
              <i className="fas fa-pen"></i>
            </Button>
            <UncontrolledTooltip placement="top" target={`edit-${rowId}`}>
              Editar
            </UncontrolledTooltip>

            <Button id={`clone-${rowId}`} color="info" size="sm" className="me-1 px-2 py-1" onClick={() => handleClone(orden)} title="Clonar orden">
              <i className="fas fa-clone"></i>
            </Button>

            <SolicitarMaterialButton 
              ordenId={orden.id}
              disabled={false}
              onMaterialRequested={() => {
                reloadOrdenes();
              }}
            />

            <Button id={`pdf-${rowId}`} color="success" size="sm" className="me-1 px-2 py-1" onClick={() => handlePdf(orden)}>
              <i className="fas fa-file-pdf"></i>
            </Button>
            <UncontrolledTooltip placement="top" target={`pdf-${rowId}`}>
              Generar PDF
            </UncontrolledTooltip>

            {/* Botón de imprimir solo para órdenes externas */}
            {orden.trabajo_interno === false && (
              <>
                <Button id={`print-${rowId}`} color="secondary" size="sm" className="me-1 px-2 py-1" onClick={() => handlePrintExterna(orden)}>
                  <i className="fas fa-print"></i>
                </Button>
                <UncontrolledTooltip placement="top" target={`print-${rowId}`}>
                  Imprimir PDF sin cliente ni asesor
                </UncontrolledTooltip>
              </>
            )}

            <IniciarOrdenButton 
              orden={orden}
              onReload={reloadOrdenes}
              onShowAlert={showAlert}
              logger={ordenLogger}
            />

            <ReturnOrderButton
              orden={orden}
              onReturnOrder={reloadOrdenes}
              onShowAlert={showAlert}
            />

            <ViewNotesButton
              orden={orden}
            />

            <Button id={`delete-${rowId}`} color="danger" size="sm" className="px-2 py-1" onClick={() => handleRemove(orden)}>
              <i className="fas fa-eraser"></i>
            </Button>
            <UncontrolledTooltip placement="top" target={`delete-${rowId}`}>
              Eliminar
            </UncontrolledTooltip>
          </div>
        );
      },
      enableSorting: false,
      enableColumnFilter: false,
      meta: {
        style: {
          width: "160px",
          maxWidth: "160px",
          minWidth: "160px",
          textAlign: "center",
          whiteSpace: "nowrap",
          overflow: "visible"
        }
      },
    },
  ], [clientesMap, materialesMap, medidasMap]);



  // Utilidad: tipos globales por recubrimiento (para selects)
  const tiposPorRecubrimiento = useMemo(() => {
    const map = {};
    recubrimientos.forEach(r => {
      map[r.id] = recubrimientoTiposGlobal.filter(rt => rt.recubrimiento === r.id);
    });
    return map;
  }, [recubrimientos, recubrimientoTiposGlobal]);

  // Handlers
  const handleAdd = async () => {
    // Ensure we have medidas loaded first
    if (medidas.length === 0) {
      try {
        ordenLogger.debug("Cargando medidas antes de crear orden");
        const medidasData = await RelatedDataHelper.getMedidas();
        ordenLogger.debug("Medidas cargadas para nueva orden", { count: medidasData.length });
        setMedidas(medidasData);
      } catch (err) {
        ordenLogger.error("Error cargando medidas para nueva orden", err);
      }
    }
    
    // Load all parameter collections when opening the modal
    try {
      ordenLogger.debug("Cargando parámetros para modal de creación");
      const parametrosProceso = await RelatedDataHelper.getAllParametrosProceso();
      const { brillo: brilloSubtipos, plastificado: plastificadoSubtipos, pegue: pegueSubtipos, 
              troquelado: troqueladoSubtipos, acabado: acabadoSubtipos } = parametrosProceso;
      
      // Update all state variables
      setParametrosBrilloInterno(brilloSubtipos);
      setParametrosPlastificadoInterno(plastificadoSubtipos);
      setParametrosPegueInterno(pegueSubtipos);
      setParametrosTroqueladoInterno(troqueladoSubtipos);
      setParametrosAcabadoInterno(acabadoSubtipos);
      
      // Cargar parámetros de tinta (solo activos)
      const parametrosTintaRepository = RepositoryFactory.getRepository('parametros_tinta');
      const parametrosTintaData = await parametrosTintaRepository.getAllActive();
      setParametrosTinta(parametrosTintaData);
      
      ordenLogger.debug("Parámetros cargados para modal", { 
        brillo: brilloSubtipos.length,
        plastificado: plastificadoSubtipos.length,
        pegue: pegueSubtipos.length,
        troquelado: troqueladoSubtipos.length,
        acabado: acabadoSubtipos.length
      });
    } catch (err) {
      ordenLogger.error("Error cargando parámetros para modal", err);
      setModalError("Error al cargar los parámetros. Intente nuevamente.");
    }
    
    // Obtenemos el ID de la medida desde planeación si está disponible
    const medidaId = planeacionForm.planeacion_tamano || "";
    
    // En la nueva estructura, cada corte_conversion tiene sus propios parametros
    // y cada parametro tiene un campo de impresion asociado
    const initialCorteConversion = [{
      // ID will be assigned by PocketBase
      papel: "",
      parametros: [{ 
        // ID will be assigned by PocketBase
        cantidad: "", 
        sobrante: "", 
        total: "",
        tamano_corte: "",
        tamano_cortado: "",
        detalles: "",
        // Datos de impresion asociados directamente al parametro
        impresion: {
          // ID will be assigned by PocketBase
          impresos: "", 
          sobrantes: "", 
          total: "",
          tamano: medidaId, // Usamos el ID de la medida desde planeación
          pasadas: "",
          tinta_color: ""
        }
      }],
      isOpen: true
    }];
    
    // Creamos un array de impresión basado en la estructura de corte_conversion
    // para mantener la compatibilidad con la UI existente
    const initialImpresion = initialCorteConversion.map(corte => {
      return {
        tipo_papel: corte.papel,
        parametros: corte.parametros.map(parametro => {
          return {
            impresos: parametro.cantidad || "",
            sobrantes: parametro.sobrante || "",
            total: parametro.total || "",
            tamano: medidaId,
            pasadas: "",
            tinta_color: "",
            montaje: ""
          };
        }),
        isOpen: true
      };
    });

    // Guardamos initialImpresion en impresionForm para la UI
    setImpresionForm(initialImpresion);

    setForm({
      cliente: "",
      asesor_comercial: "",
      nombre_producto: "",
      fecha_inicio: getNow(),
      fecha_entrega: getNow(),
      corte_conversion: initialCorteConversion,
      // Ya no necesitamos guardar impresion aquí, la información está dentro de corte_conversion
      corte_facturacion: getEndOfMonth(),
      facturacion: "",
      cantidad_solicitada: "",
      oc: "",
      trabajo_interno: false,
      reimpresion: false,
      tipo_producto: "",
      solicitud: "",
      referencia_trabajo: "",
      tipo_papel: "",
      tamano_papel: "",
      cabida_preprensa: "",
      tintas: "",
      detalles: "",
      cantidad: "",
      sobrantes: "",
      tamano_corte: "",
      troquel_n: false,
      troquel_e: false,
      pliegos: "",
      acabado_general: "",
      observaciones: "",
      // Campos de impresión
      impresion_pliegos: "",
      impresion_impresos: "",
      impresion_sobrantes: "",
      impresion_tintas: "",
      impresion_tiro: false,
      impresion_retiro: false,
      impresion_observaciones: "",

      // Troquelado external component has been removed
      // Repuje has been completely removed
      // Estampado has been completely removed
    });
    setRecubrimientoSeleccion({});
    setNuevoTipo({});
    setAgregarExistente({});
    setModalError("");
    
    // Recalculamos los datos de impresión a partir del corte_conversion actual
    if (initialCorteConversion) {
      const newImpresionData = initialCorteConversion.map(corte => ({
        tipo_papel: corte.papel,
        parametros: corte.parametros.map(parametro => ({
          impresos: parametro.cantidad || "",
          sobrantes: parametro.sobrante || "",
          total: parametro.total || "",
          tamano: parametro.impresion?.tamano || "",
          pasadas: parametro.impresion?.pasadas || "",
          tinta_color: parametro.impresion?.tinta_color || "",
          montaje: parametro.impresion?.montaje || ""
        })),
        isOpen: corte.isOpen || true
      }));
      setImpresionForm(newImpresionData);
    } else {
      setImpresionForm([]);
    }
    setPlastificadoForm({
      plastificado_interno: [], // Array de IDs de parametros_plastificado_interno seleccionados
    });
    setAcabadoForm({
      acabado_interno: [], // Array de IDs de parametros_acabado_interno seleccionados
    });
    // Repuje has been completely removed
    setPegueForm({
      pegue_interno: [], // Initialize pegue_interno array
    });
    setBrilloForm({
      brillo_interno: [], // Inicializar campo para los subtipos de brillo
    });
    setTroqueladoForm({
      troquelado_interno: [], // Only keeping troquelado_interno field
    });
    setPlaneacionForm({
      planeacion_id: "",
      planeacion_abierto_ancho: "",
      planeacion_abierto_alto: "",
      planeacion_armado_ancho: "",
      planeacion_armado_alto: "",
      planeacion_armado_prof: "",
      planeacion_tintas: "",
      planeacion_lineatura: "",
      planeacion_tipo_pinza: "",
      planeacion_troquel_e: false,
      planeacion_troquel_n: false,
      planeacion_numero_troquel: "",

      planeacion_tamano: "",
      planeacion_cabida_preprensa: "",
    });
    
    // Ensure recubrimientos are loaded before opening modal
    try {
      ordenLogger.debug("Refrescando datos de recubrimientos");
      // Limpiar cache de recubrimientos para obtener datos frescos
      
      const recubrs = await RelatedDataHelper.getRecubrimientos();
      
      // Custom sort order: Brillo, Plastificado, Troquelado, Pegue/Pegado/Pegue en Maquina, Acabado
      const customOrder = {
        "BRILLO": 1,
        "PLASTIFICADO": 2,
        "TROQUELADO": 3,
        "PEGUE": 4,
        "PEGADO": 4,
        "PEGUE EN MAQUINA": 4,
        "ACABADO": 5
      };
      
      recubrs.sort((a, b) => {
        const aOrder = customOrder[a.nombre?.toUpperCase()] || 999;
        const bOrder = customOrder[b.nombre?.toUpperCase()] || 999;
        return aOrder - bOrder || a.nombre?.localeCompare(b.nombre);
      });
      
      // Verificar si ya existe un recubrimiento con nombre "ACABADO"
      const acabadoExiste = recubrs.some(r => r.nombre && r.nombre.toUpperCase() === "ACABADO");
      
      // Si no existe, agregar uno manualmente para la interfaz
      if (!acabadoExiste) {
        recubrs.push({
          id: "acabado_temp_id", // ID temporal
          nombre: "ACABADO",
          estado: true
        });
        
        // Re-ordenar después de agregar
        recubrs.sort((a, b) => {
          const aOrder = customOrder[a.nombre?.toUpperCase()] || 999;
          const bOrder = customOrder[b.nombre?.toUpperCase()] || 999;
          return aOrder - bOrder || a.nombre?.localeCompare(b.nombre);
        });
      }
      
      setRecubrimientos(recubrs);
      ordenLogger.debug("Recubrimientos configurados", { count: recubrs.length });
    } catch (err) {
      ordenLogger.error("Error refrescando recubrimientos", err);
    }
    
    setModalAdd(true);
  };

  const handleEdit = async (orden) => {
    try {
      // Ensure we have medidas loaded first
      if (medidas.length === 0) {
        try {
          ordenLogger.debug("Cargando medidas para edición");
          const medidasData = await RelatedDataHelper.getMedidas();
          ordenLogger.debug("Medidas cargadas", { count: medidasData.length });
          setMedidas(medidasData);
        } catch (err) {
          ordenLogger.error("Error cargando medidas para edición", err);
        }
      } else {
        ordenLogger.debug("Medidas ya cargadas", { count: medidas.length });
      }
      
      // Load all parameter collections when opening the edit modal
      try {
        ordenLogger.debug("Cargando parámetros para modal de edición");
        const parametrosProceso = await RelatedDataHelper.getAllParametrosProceso();
        const { brillo: brilloSubtipos, plastificado: plastificadoSubtipos, pegue: pegueSubtipos, 
                troquelado: troqueladoSubtipos, acabado: acabadoSubtipos } = parametrosProceso;
        
        // Update all state variables
        setParametrosBrilloInterno(brilloSubtipos);
        setParametrosPlastificadoInterno(plastificadoSubtipos);
        setParametrosPegueInterno(pegueSubtipos);
        setParametrosTroqueladoInterno(troqueladoSubtipos);
        setParametrosAcabadoInterno(acabadoSubtipos);
        
        // Cargar parámetros de tinta
        const parametrosTintaRepository = RepositoryFactory.getRepository('parametros_tinta');
        const parametrosTintaData = await parametrosTintaRepository.getAllActive();
        setParametrosTinta(parametrosTintaData);
        
        ordenLogger.debug("Parámetros cargados para edición", {
          brillo: brilloSubtipos.length,
          plastificado: plastificadoSubtipos.length,
          pegue: pegueSubtipos.length,
          troquelado: troqueladoSubtipos.length,
          acabado: acabadoSubtipos.length
        });
      } catch (err) {
        ordenLogger.error("Error cargando parámetros para edición", err);
      }
      
      // Ensure recubrimientos are loaded
      try {
        ordenLogger.debug("Refrescando recubrimientos para edición");
        // Limpiar cache de recubrimientos para obtener datos frescos

        const recubrs = await RelatedDataHelper.getRecubrimientos();
        
        // Custom sort order: Brillo, Plastificado, Troquelado, Pegue/Pegado/Pegue en Maquina, Acabado
        const customOrder = {
          "BRILLO": 1,
          "PLASTIFICADO": 2,
          "TROQUELADO": 3,
          "PEGUE": 4,
          "PEGADO": 4,
          "PEGUE EN MAQUINA": 4,
          "ACABADO": 5
        };
        
        recubrs.sort((a, b) => {
          const aOrder = customOrder[a.nombre?.toUpperCase()] || 999;
          const bOrder = customOrder[b.nombre?.toUpperCase()] || 999;
          return aOrder - bOrder || a.nombre?.localeCompare(b.nombre);
        });
        
        // Verificar si ya existe un recubrimiento con nombre "ACABADO"
        const acabadoExiste = recubrs.some(r => r.nombre && r.nombre.toUpperCase() === "ACABADO");
        
        // Si no existe, agregar uno manualmente para la interfaz
        if (!acabadoExiste) {
          recubrs.push({
            id: "acabado_temp_id", // ID temporal
            nombre: "ACABADO",
            estado: true
          });
          
          // Re-ordenar después de agregar
          recubrs.sort((a, b) => {
            const aOrder = customOrder[a.nombre?.toUpperCase()] || 999;
            const bOrder = customOrder[b.nombre?.toUpperCase()] || 999;
            return aOrder - bOrder || a.nombre?.localeCompare(b.nombre);
          });
        }
        
        ordenLogger.debug("Recubrimientos configurados para edición", { count: recubrs.length });
        setRecubrimientos(recubrs);
      } catch (err) {
        ordenLogger.error("Error refrescando recubrimientos para edición", err);
      }

      // Load planeacion data if exists
      let planeacion = {};
      if (orden.planeacion) {
        try {
          planeacion = await pb.collection("planeacion").getOne(orden.planeacion);
        } catch (err) {
          // Error silencioso
        }
      }
      
      // Load corte_conversion data if exists
      let corteConversionData = [];
      if (Array.isArray(orden.corte_conversion) && orden.corte_conversion.length > 0) {
        try {
          // Obtener todos los registros de corte_conversion relacionados con la orden
          const cortePromises = orden.corte_conversion.map(async (corteId) => {
            try {
              const corte = await pb.collection("corte_conversion").getOne(corteId);
              
              // Para cada corte, obtenemos sus parámetros asociados
              if (Array.isArray(corte.parametro) && corte.parametro.length > 0) {
                const parametrosPromises = corte.parametro.map(async (parametroId) => {
                  try {
                    const paramRecord = await pb.collection("corte_conversion_tabla").getOne(parametroId);
                    
                    // Si el parámetro tiene una relación con impresión, cargamos también esos datos
                    let impresionData = null;
                    if (paramRecord.impresion) {
                      try {
                        ordenLogger.debug("Cargando datos de impresión", { impresionId: paramRecord.impresion });
                        impresionData = await pb.collection("impresion_tabla").getOne(paramRecord.impresion, {
                          expand: 'parametros_tinta'
                        });
                        ordenLogger.debug("Datos de impresión cargados correctamente", { 
                          parametrosTintaCount: Array.isArray(impresionData.parametros_tinta) ? impresionData.parametros_tinta.length : 0 
                        });
                      } catch (impErr) {
                        ordenLogger.error("Error cargando datos de impresión", { impresionId: paramRecord.impresion, error: impErr });
                      }
                    }
                    
                    return {
                      ...paramRecord,
                      impresion: impresionData // Agregamos el objeto completo de impresión
                    };
                  } catch (err) {
                    ordenLogger.error("Error cargando parámetro", { parametroId, error: err });
                    return null;
                  }
                });
                
                const parametros = (await Promise.all(parametrosPromises)).filter(Boolean);
                
                // Formateamos los datos como los espera nuestro componente
                return {
                  id: corte.id,
                  papel: corte.papel,
                  parametros: parametros.map(p => ({
                    id: p.id,
                    cantidad: p.cantidad || "",
                    sobrante: p.sobrante || "",
                    total: p.total || "",
                    tamano_corte: p.tamano_corte || "",
                    tamano_cortado: p.tamano_cortado || "",
                    detalles: p.detalles || "",
                    // Incluimos la impresión si existe
                    impresion: p.impresion ? {
                      id: p.impresion.id,
                      impresos: p.impresion.impresos || "",
                      sobrantes: p.impresion.sobrantes || "",
                      total: p.impresion.total || "",
                      tamano: p.impresion.tamano || "",
                      pasadas: p.impresion.pasadas || "",
                      tinta_color: p.impresion.tinta_color || "",

                    } : null
                  })),
                  isOpen: true // Para controlar la expansión/colapso en la UI
                };
              }
              return null;
            } catch (err) {
              ordenLogger.error("Error obteniendo datos de corte", err);
              return null;
            }
          });
          
          corteConversionData = (await Promise.all(cortePromises)).filter(Boolean);
        } catch (err) {
          ordenLogger.error("Error cargando datos de corte y conversión", err);
        }
      }
      
      // Si no hay datos o hubo un error, inicializamos con un corte vacío
      if (corteConversionData.length === 0) {
        corteConversionData = [{
          // ID will be assigned by PocketBase
          papel: "",
          parametros: [{ 
            // ID will be assigned by PocketBase
            cantidad: "", 
            sobrante: "", 
            total: "",
            tamano_corte: "",
            tamano_cortado: "",
            detalles: ""
          }],
          isOpen: true
        }];
      }
      
      // Actualizar el estado del formulario de corte
      setCorteForm(corteConversionData);
      
      // En el nuevo modelo de datos, la impresión está directamente asociada a cada parámetro de corte_conversion_tabla
      // Por lo tanto, debemos extraer esta información de los datos de corte_conversion ya cargados
      let impresionData = [];
      
      try {
        ordenLogger.debug("Generando datos de impresión desde corte_conversion", { cortesCount: corteConversionData.length });
        
        // Inicializamos un mapa para agrupar por tipo de papel
        const papelMap = new Map();
        
        // Para cada corte en corteConversionData
        for (const corte of corteConversionData) {
          if (!corte.papel || !Array.isArray(corte.parametros)) continue;
          
          // Extraer información de impresión de cada parámetro
          const parametrosImpresion = [];
          
          for (const parametro of corte.parametros) {
            // Verificar si el parámetro tiene asociada una impresión
            if (parametro.impresion) {
              // Si es un objeto completo de impresión (cargado de la BD)
              let impresionParam = {
                id: typeof parametro.impresion === 'object' ? parametro.impresion.id : parametro.impresion,
                impresos: parametro.cantidad || "",
                sobrantes: parametro.sobrante || "",
                total: parametro.total || "",
                tamano: "",
                pasadas: "",
                tinta_color: "",
                montaje: ""
              };
              
              // Si impresion es un ID, necesitamos cargar los datos completos
              if (typeof parametro.impresion === 'string') {
                try {
                  ordenLogger.debug("Cargando datos de impresión por parámetro", { impresionId: parametro.impresion });
                  const impresionRecord = await pb.collection('impresion_tabla').getOne(parametro.impresion, {
                    expand: 'parametros_tinta'
                  });
                  impresionParam = {
                    id: impresionRecord.id,
                    impresos: impresionRecord.impresos || "",
                    sobrantes: impresionRecord.sobrantes || "",
                    total: impresionRecord.total || "",
                    tamano: impresionRecord.tamano || "",
                    pasadas: impresionRecord.pasadas || "",
                    tinta_color: impresionRecord.tinta_color || "",
                    parametros_tinta: Array.isArray(impresionRecord.parametros_tinta) ? impresionRecord.parametros_tinta : []
                  };
                  ordenLogger.debug("Datos de impresión procesados para parámetro", { 
                    parametrosTintaCount: impresionParam.parametros_tinta.length 
                  });
                } catch (err) {
                  ordenLogger.error("Error cargando datos de impresión por parámetro", { impresionId: parametro.impresion, error: err });
                }
              } else if (typeof parametro.impresion === 'object') {
                // Ya tenemos el objeto de impresión, simplemente extraemos sus propiedades
                const imp = parametro.impresion;
                impresionParam = {
                  id: imp.id,
                  impresos: imp.impresos || parametro.cantidad || "",
                  sobrantes: imp.sobrantes || parametro.sobrante || "",
                  total: imp.total || parametro.total || "",
                  tamano: imp.tamano || "",
                  pasadas: imp.pasadas || "",
                  tinta_color: imp.tinta_color || "",
                  parametros_tinta: Array.isArray(imp.parametros_tinta) ? imp.parametros_tinta : []
                };
              }
              
              parametrosImpresion.push(impresionParam);
            } else {
              // Si no hay impresión asociada, creamos una con los datos básicos del parámetro
              parametrosImpresion.push({
                impresos: parametro.cantidad || "",
                sobrantes: parametro.sobrante || "",
                total: parametro.total || "",
                tamano: "",
                pasadas: "",
                tinta_color: "",
                montaje: ""
              });
            }
          }
          
          // Agregamos o actualizamos la entrada en el mapa para este tipo de papel
          if (papelMap.has(corte.papel)) {
            // Si ya existe una entrada para este papel, agregamos los parámetros
            const existingParams = papelMap.get(corte.papel).parametros;
            papelMap.get(corte.papel).parametros = [...existingParams, ...parametrosImpresion];
          } else {
            // Si no existe, creamos una nueva entrada
            papelMap.set(corte.papel, {
              id: corte.id, // Usamos el ID del corte como ID de la impresión
              tipo_papel: corte.papel,
              parametros: parametrosImpresion,
              isOpen: true
            });
          }
        }
        
        // Convertimos el mapa a un array
        impresionData = Array.from(papelMap.values());
        ordenLogger.debug("Datos de impresión generados", { count: impresionData.length });
      } catch (err) {
        ordenLogger.error("Error generando datos de impresión", err);
      }
      
          // Si no hay datos o hubo un error, pero sí tenemos datos de corte, usamos esos para generar datos de impresión
      if (impresionData.length === 0 && corteConversionData.length > 0) {
        ordenLogger.debug("Generando datos de impresión desde corte_conversion como fallback");
        
        // Inicializamos un mapa para agrupar por tipo de papel
        const papelMap = new Map();
        
        // Para cada corte en corteConversionData
        for (const corte of corteConversionData) {
          if (!corte.papel || !Array.isArray(corte.parametros)) continue;
          
          // Extraer información de impresión de cada parámetro
          const parametrosImpresion = [];
          
          for (const parametro of corte.parametros) {
            // Verificar si el parámetro tiene asociada una impresión
            if (parametro.impresion) {
              parametrosImpresion.push({
                id: parametro.impresion.id,
                impresos: parametro.impresion.impresos || "",
                sobrantes: parametro.impresion.sobrantes || "",
                total: parametro.impresion.total || "",
                tamano: parametro.impresion.tamano || "",
                pasadas: parametro.impresion.pasadas || "",
                tinta_color: parametro.impresion.tinta_color || "",
                montaje: parametro.impresion.montaje || ""
              });
            } else {
              // Si no hay impresión asociada, creamos una con los datos básicos del parámetro
              parametrosImpresion.push({
                impresos: parametro.cantidad || "",
                sobrantes: parametro.sobrante || "",
                total: parametro.total || "",
                tamano: "",
                pasadas: "",
                tinta_color: "",
                montaje: ""
              });
            }
          }
          
          // Agregamos esta entrada al mapa
          papelMap.set(corte.papel, {
            tipo_papel: corte.papel,
            parametros: parametrosImpresion,
            isOpen: true
          });
        }
        
        // Convertimos el mapa a un array
        impresionData = Array.from(papelMap.values());
        ordenLogger.debug("Datos de impresión generados desde corte_conversion", { count: impresionData.length });
      } else if (impresionData.length === 0) {
        // Si no hay datos de ningún tipo, inicializamos con un valor predeterminado vacío
        impresionData = [{
          tipo_papel: "",
          parametros: [{ 
            impresos: "", 
            sobrantes: "", 
            total: "",
            tamano: "",
            pasadas: "",
            tinta_color: "",
            montaje: ""
          }],
          isOpen: true
        }];
      }
      
      // Actualizar el estado del formulario de impresión
      setImpresionForm(impresionData);
      
      ordenLogger.debug("Datos de impresión procesados", { count: impresionData.length });      // Ahora debemos sincronizar estos datos con la estructura de corte_conversion
      // para que cada parámetro tenga su información de impresión correspondiente
      // y asegurarnos que los tipos de papel están correctamente configurados
      if (corteConversionData && corteConversionData.length > 0) {
        // Crear impresiones para cada corte si no existen
        if (!impresionData || impresionData.length === 0) {
          impresionData = corteConversionData.map(corte => ({
            tipo_papel: corte.papel || "",
            parametros: corte.parametros.map(param => ({
              impresos: param.cantidad || "",
              sobrantes: param.sobrante || "",
              total: param.total || "",
              tamano: param.impresion?.tamano || planeacionForm.planeacion_tamano || "",
              pasadas: param.impresion?.pasadas || "",
              tinta_color: param.impresion?.tinta_color || "",
              montaje: param.impresion?.montaje || ""
            })),
            isOpen: true
          }));
          
          // Actualizar el estado del formulario de impresión con estos nuevos datos
          ordenLogger.debug("Datos de impresión creados desde corte_conversion", { count: impresionData.length });
        }
        // Asegurarnos que tenemos suficientes impresiones para cada corte
        else {
          // Añadir impresiones faltantes
          while (impresionData.length < corteConversionData.length) {
            const corteIndex = impresionData.length;
            const corte = corteConversionData[corteIndex];
            
            impresionData.push({
              tipo_papel: corte.papel || "",
              parametros: corte.parametros.map(param => ({
                impresos: param.cantidad || "",
                sobrantes: param.sobrante || "",
                total: param.total || "",
                tamano: param.impresion?.tamano || planeacionForm.planeacion_tamano || "",
                pasadas: param.impresion?.pasadas || "",
                tinta_color: param.impresion?.tinta_color || "",
                montaje: param.impresion?.montaje || ""
              })),
              isOpen: true
            });
          }
          
          // Asegurarnos que los tipos de papel coincidan
          impresionData.forEach((impresion, idx) => {
            if (idx < corteConversionData.length) {
              if (!impresion.tipo_papel && corteConversionData[idx].papel) {
                impresion.tipo_papel = corteConversionData[idx].papel;
              } else if (impresion.tipo_papel && !corteConversionData[idx].papel) {
                corteConversionData[idx].papel = impresion.tipo_papel;
              }
              
              // Asegurarnos que cada impresión tenga suficientes parámetros
              while (impresion.parametros.length < corteConversionData[idx].parametros.length) {
                const paramIndex = impresion.parametros.length;
                const paramCorte = corteConversionData[idx].parametros[paramIndex];
                
                impresion.parametros.push({
                  impresos: paramCorte.cantidad || "",
                  sobrantes: paramCorte.sobrante || "",
                  total: paramCorte.total || "",
                  tamano: paramCorte.impresion?.tamano || planeacionForm.planeacion_tamano || "",
                  pasadas: paramCorte.impresion?.pasadas || "",
                  tinta_color: paramCorte.impresion?.tinta_color || "",
                  montaje: paramCorte.impresion?.montaje || ""
                });
              }
            }
          });
        }
        
        // Usamos corteConversionData que acabamos de cargar en lugar de form.corte_conversion
        const updatedCorteConversion = JSON.parse(JSON.stringify(corteConversionData));
        
        // Para cada impresión, buscamos el corte correspondiente por tipo de papel
        impresionData.forEach((impresion, impIndex) => {
          // Primero intentamos encontrar por índice para mantener el orden
          if (impIndex < updatedCorteConversion.length) {
            const corte = updatedCorteConversion[impIndex];
            
            // Si el tipo de papel coincide o uno de ellos está vacío, usamos este corte
            if (!impresion.tipo_papel || !corte.papel || impresion.tipo_papel === corte.papel) {
              // Si alguno está vacío, copiamos el valor del otro
              if (!impresion.tipo_papel) {
                impresion.tipo_papel = corte.papel;
              } else if (!corte.papel) {
                corte.papel = impresion.tipo_papel;
              }
              
              // Sincronizar parámetros
              impresion.parametros.forEach((paramImpresion, paramIndex) => {
                if (paramIndex < corte.parametros.length) {
                  const paramCorte = corte.parametros[paramIndex];
                  
                  // Inicializar el objeto impresion si no existe
                  if (!paramCorte.impresion) {
                    paramCorte.impresion = {};
                  }
                  
                  // Sincronizar datos de impresión con el parámetro
                  paramCorte.impresion = {
                    impresos: paramImpresion.impresos,
                    sobrantes: paramImpresion.sobrantes,
                    total: paramImpresion.total,
                    tamano: paramImpresion.tamano,
                    pasadas: paramImpresion.pasadas,
                    tinta_color: paramImpresion.tinta_color,
                    montaje: paramImpresion.montaje
                  };
                  
                  // Sincronizar también cantidad y sobrante en el parámetro
                  paramCorte.cantidad = paramImpresion.impresos;
                  paramCorte.sobrante = paramImpresion.sobrantes;
                  paramCorte.total = paramImpresion.total;
                }
              });
              
              // Marcamos esta impresión como procesada
              impresion._processed = true;
            }
          }
          
          // Solo procedemos con la búsqueda por tipo de papel si la impresión no ha sido procesada
          if (impresion._processed) {
            delete impresion._processed;
            return;
          }
          
          // Si no encontramos por índice, buscamos por tipo de papel
          const corteIndex = updatedCorteConversion.findIndex(corte => 
            corte.papel === impresion.tipo_papel
          );
          
          if (corteIndex !== -1) {
            // Si encontramos un corte que coincide, actualizamos sus parámetros
            impresion.parametros.forEach((paramImpresion, paramIndex) => {
              const paramCorte = updatedCorteConversion[corteIndex].parametros[paramIndex];
              
              if (paramCorte) {
                // Inicializar el objeto impresion si no existe
                if (!paramCorte.impresion) {
                  paramCorte.impresion = {};
                }
                
                // Actualizar datos
                paramCorte.impresion = {
                  impresos: paramImpresion.impresos,
                  sobrantes: paramImpresion.sobrantes,
                  total: paramImpresion.total,
                  tamano: paramImpresion.tamano,
                  pasadas: paramImpresion.pasadas,
                  tinta_color: paramImpresion.tinta_color,
                  montaje: paramImpresion.montaje
                };
                
                paramCorte.cantidad = paramImpresion.impresos;
                paramCorte.sobrante = paramImpresion.sobrantes;
                paramCorte.total = paramImpresion.total;
              }
            });
          } else if (impresion.tipo_papel) {
            // Si no encontramos un corte para esta impresión, creamos uno nuevo
            updatedCorteConversion.push({
              papel: impresion.tipo_papel,
              parametros: impresion.parametros.map(param => ({
                cantidad: param.impresos || "",
                sobrante: param.sobrantes || "",
                total: param.total || "",
                tamano_corte: "",
                tamano_cortado: "",
                detalles: "",
                impresion: {
                  impresos: param.impresos || "",
                  sobrantes: param.sobrantes || "",
                  total: param.total || "",
                  tamano: param.tamano || "",
                  pasadas: param.pasadas || "",
                  tinta_color: param.tinta_color || "",
                  montaje: param.montaje || ""
                }
              })),
              isOpen: true
            });
          }
        });
        
        // Actualizamos el estado del formulario de impresión
        setImpresionForm(impresionData);
        
        // Actualizamos corteForm con los datos sincronizados
        setCorteForm(updatedCorteConversion);
        
        // Actualizamos también el estado del formulario principal
        setForm(prev => ({
          ...prev,
          corte_conversion: updatedCorteConversion
        }));
      }

      // Set plastificado form data
      // Set plastificado form data (only plastificado_interno is kept)
      setPlastificadoForm({
        // plastificado_interno comes from the orden_produccion record, not from the plastificado record
        plastificado_interno: Array.isArray(orden.plastificado_interno) ? orden.plastificado_interno : [],
      });

      // Case & Cure has been completely removed

      // Repuje has been completely removed

      // Estampado has been completely removed

      // Set pegue form data (only pegue_interno is kept)
      setPegueForm({
        // pegue_interno comes from the orden_produccion record, not from the pegue record
        pegue_interno: Array.isArray(orden.pegue_interno) ? orden.pegue_interno : [],
      });

      // Set brillo form data (only brillo_interno is kept)
      setBrilloForm({
        brillo_interno: Array.isArray(orden.brillo_interno) ? orden.brillo_interno : [], // Aseguramos que sea un array
      });
      
      // Set acabado form data
      setAcabadoForm({
        acabado_interno: Array.isArray(orden.acabado_interno) ? orden.acabado_interno : [], // Aseguramos que sea un array
      });

      // Set planeacion form data
      ordenLogger.debug("Datos de planeación cargados", { tamano: planeacion.tamano });
      
      setPlaneacionForm({
        planeacion_id: planeacion.id || "",
        planeacion_abierto_ancho: planeacion.abierto_ancho || "",
        planeacion_abierto_alto: planeacion.abierto_alto || "",
        planeacion_armado_ancho: planeacion.armado_ancho || "",
        planeacion_armado_alto: planeacion.armado_alto || "",
        planeacion_armado_prof: planeacion.armado_prof || "",
        planeacion_tintas: planeacion.tintas || "",
        parametros_tinta: Array.isArray(planeacion.parametros_tinta) ? planeacion.parametros_tinta : [],
        planeacion_lineatura: planeacion.lineatura || "",
        planeacion_tipo_pinza: planeacion.tipo_pinza || "",
        planeacion_troquel_e: planeacion.troquel_e || false,
        planeacion_troquel_n: planeacion.troquel_n || false,
        planeacion_numero_troquel: planeacion.numero_troquel !== null && planeacion.numero_troquel !== undefined ? parseInt(planeacion.numero_troquel, 10) : "",
  
        planeacion_tamano: planeacion.tamano || "", // RELATION_RECORD_ID - ID de relación a la colección 'medida'
        planeacion_cabida_preprensa: planeacion.cabida_preprensa || "",
      });

      // Set main form data
      ordenLogger.debug("Configurando datos del formulario", { corteConversionData });

      setForm({
        numero: orden.numero,
        cliente: orden.cliente,
        asesor_comercial: orden.asesor_comercial || "",
        nombre_producto: orden.nombre_producto,
        fecha_inicio: toInputDate(orden.fecha_inicio),
        fecha_entrega: toInputDate(orden.fecha_entrega),
        corte_facturacion: toInputDate(orden.corte_facturacion),
        facturacion: orden.facturacion,
        cantidad_solicitada: orden.cantidad_solicitada,
        oc: orden.oc,
        trabajo_interno: Boolean(orden.trabajo_interno),
        reimpresion: Boolean(orden.reimpresion),
        tipo_producto: orden.producto || "", // Corregido: leer producto en lugar de tipo_producto
        solicitud: orden.solicitud || "",
        referencia_trabajo: form.referencia_trabajo,
        tipo_papel: orden.tipo_papel,
        tamano_papel: orden.tamano_papel,
        cabida_preprensa: form.cabida_preprensa,
        tintas: form.tintas,
        detalles: form.detalles,
        cantidad: form.cantidad,
        sobrantes: form.sobrantes,
        tamano_corte: form.tamano_corte,
        troquel_n: form.troquel_n,
        troquel_e: form.troquel_e,
        pliegos: form.pliegos,
        acabado_general: form.acabado_general,
        observaciones: form.observaciones,
        numero_reproceso: orden.numero_reproceso || "",
        corte_conversion: corteConversionData, // Agregar datos de corte y conversión
        // Campos de impresión
        impresion_pliegos: "",
        impresion_impresos: "",
        impresion_sobrantes: "",
        impresion_tintas: "",
        impresion_tiro: false,
        impresion_retiro: false,
        impresion_observaciones: "",
        // Brillo collection fields removed as the collection no longer exists
        // Campos de Troquelado
        // Troquelado external component has been removed
        // Only keeping troquelado_interno field
        troquelado_interno: Array.isArray(orden.troquelado_interno) ? orden.troquelado_interno : [],
      });

      // Initialize troqueladoForm state - only keeping troquelado_interno
      setTroqueladoForm({
        troquelado_interno: Array.isArray(orden.troquelado_interno) ? orden.troquelado_interno : [],
      });

      // Cargar recubrimientos seleccionados para la orden
      const seleccion = {};
      if (orden.recubrimiento && Array.isArray(orden.recubrimiento)) {
        orden.recubrimiento.forEach(id => {
          const obj = recubrimientoTiposGlobal.find(rt => rt.id === id);
          if (obj) {
            if (!seleccion[obj.recubrimiento]) seleccion[obj.recubrimiento] = [];
            seleccion[obj.recubrimiento].push(obj);
          }
        });
      }
      setRecubrimientoSeleccion(seleccion);
      setNuevoTipo({});
      setAgregarExistente({});
      setEditId(orden.id);
      setModalError("");
      ordenLogger.debug("Abriendo modal de edición", { ordenId: orden.id });
      setModalEdit(true);
    } catch (err) {
      setModalError("Error al cargar los datos de la orden");
    }
  };

  /**
   * Handles cloning an existing order
   * Creates a new order directly with the same data but new sequence number
   * @param {Object} orden - The order to clone
   */
  const handleClone = async (orden) => {
    await handleCloneDirect(
      orden,
      pb,
      ordenRepository,
      showAlert,
      reloadOrdenes,
      ordenLogger,
      setSaving
    );
  };

  const handleRemove = (orden) => {
    setOrdenToDelete(orden);
    setShowDeleteAlert(true);
  };

  // Función para detectar dispositivos móviles
  const detectMobile = () => {
    try {
      const userAgent = navigator.userAgent || navigator.vendor || window.opera || '';
      
      // Detectar iOS específicamente (iPhone, iPad, iPod)
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
      
      // Detectar Android específicamente
      const isAndroid = /android/i.test(userAgent);
      
      // Detectar otros dispositivos móviles comunes
      const isMobileUA = /Mobi|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
      
      // Detectar por capacidades touch
      const isTouchDevice = typeof window !== 'undefined' && (
        'ontouchstart' in window || 
        (navigator.maxTouchPoints && navigator.maxTouchPoints > 0) ||
        (window.DocumentTouch && document instanceof window.DocumentTouch)
      );
      
      // Detectar por tamaño de pantalla
      const isSmallScreen = typeof window !== 'undefined' && window.innerWidth <= 768;
      
      // Detectar por orientation API (común en móviles)
      const hasOrientationAPI = typeof window !== 'undefined' && 'orientation' in window;
      
      // Combinar todas las detecciones
      const isMobile = isIOS || isAndroid || isMobileUA || 
                      (isTouchDevice && isSmallScreen) || 
                      (isTouchDevice && hasOrientationAPI);
      
      return isMobile;
    } catch (error) {
      ordenLogger.error('Error al detectar dispositivo móvil', error);
      // En caso de error, asumir escritorio
      return false;
    }
  };

  const handlePdf = async (orden) => {
    try {
      const isMobile = detectMobile();
      
      if (isMobile) {
        // En móviles, usar el método original que funciona (generatePDF con save)
        ordenLogger.info('Dispositivo móvil detectado - usando descarga directa', { 
          ordenId: orden.id, 
          userAgent: navigator.userAgent 
        });
        
        // Mostrar indicador de carga para móviles
        setModalError(''); // Limpiar errores previos
        setGeneratingPdf(true);
        
        try {
          // Importar dinámicamente la función de impresión
          const { printOrdenProduccion } = await import('./pdf/pdf_print');
          await printOrdenProduccion(orden.id);
          
          ordenLogger.info('PDF generado exitosamente en móvil', { ordenId: orden.id });
          
          // Mostrar mensaje de éxito temporal
          setModalError('PDF generado exitosamente');
          setTimeout(() => setModalError(''), 3000);
          
        } catch (mobileError) {
          ordenLogger.error('Error al generar PDF en móvil', { error: mobileError, ordenId: orden.id });
          setModalError('Error al generar el PDF en dispositivo móvil');
        } finally {
          setGeneratingPdf(false);
        }
        
      } else {
        // En desktop, usar modal PDF
        ordenLogger.info('Dispositivo desktop detectado - usando modal PDF', { 
          ordenId: orden.id 
        });
        
        setPdfModalData({ ordenId: orden.id, filename: `orden_produccion_${orden.numero}.pdf` });
        setIsPdfModalOpen(true);
      }
    } catch (error) {
      ordenLogger.error('Error al generar PDF', { error, ordenId: orden.id });
      setModalError('Error al generar el PDF');
      setGeneratingPdf(false);
    }
  };

  const handlePrintExterna = async (orden) => {
    try {
      const isMobile = detectMobile();
      
      if (isMobile) {
        // En móviles, usar descarga directa
        ordenLogger.info('Dispositivo móvil detectado - usando descarga directa para PDF externo', { 
          ordenId: orden.id, 
          userAgent: navigator.userAgent 
        });
        
        setModalError(''); // Limpiar errores previos
        setGeneratingPdf(true);
        
        try {
          // Importar dinámicamente la función de impresión externa
          const { printOrdenProduccionExterna } = await import('./pdf/pdf_print');
          await printOrdenProduccionExterna(orden.id);
          
          ordenLogger.info('PDF externo generado exitosamente en móvil', { ordenId: orden.id });
          
          // Mostrar mensaje de éxito temporal
          setModalError('PDF externo generado exitosamente');
          setTimeout(() => setModalError(''), 3000);
          
        } catch (mobileError) {
          ordenLogger.error('Error al generar PDF externo en móvil', { error: mobileError, ordenId: orden.id });
          setModalError('Error al generar el PDF externo en dispositivo móvil');
        } finally {
          setGeneratingPdf(false);
        }
        
      } else {
        // En desktop, usar modal PDF
        ordenLogger.info('Dispositivo desktop detectado - usando modal PDF externo', { 
          ordenId: orden.id 
        });
        
        setPdfModalData({ ordenId: orden.id, filename: `orden_produccion_externa_${orden.numero}.pdf` });
        setIsPdfModalOpen(true);
      }
    } catch (error) {
      ordenLogger.error('Error al generar PDF externo', { error, ordenId: orden.id });
      setModalError('Error al generar el PDF externo');
      setGeneratingPdf(false);
    }
  };

  // Soft delete: set activo=false
  const confirmRemove = async () => {
    if (!ordenToDelete) return;
    setDeleting(true);
    try {
      await ordenRepository.deactivate(ordenToDelete.id);
      const nuevasOrdenes = ordenes.filter(o => o.id !== ordenToDelete.id);
      // Si la página actual queda vacía, retroceder una página
      const totalPages = Math.ceil(nuevasOrdenes.length / pageSize);
      if (pageIndex > 0 && pageIndex >= totalPages) {
        setPageIndex(totalPages > 0 ? totalPages - 1 : 0);
      }
      setOrdenes(nuevasOrdenes);
      setShowDeleteAlert(false);
      setOrdenToDelete(null);
    } catch (err) {
      setModalError("Error al eliminar orden");
    }
    setDeleting(false);
  };

  const cancelRemove = () => {
    setShowDeleteAlert(false);
    setOrdenToDelete(null);
  };

  // Funciones de utilidad para tracking de campos modificados (sistema isDirty)
  const markFieldAsDirty = (fieldName) => {
    setDirtyFields(prev => new Set(prev).add(fieldName));
  };

  const clearDirtyFields = () => {
    setDirtyFields(new Set());
  };

  const hasUnsavedChanges = useMemo(() => {
    return dirtyFields.size > 0;
  }, [dirtyFields]);

  const handleChange = (e) => {
    const { name, type, value, checked } = e.target;

    // Para los switches (checkboxes), usamos el valor de 'checked' en lugar de 'value'
    // Añadimos una comprobación para el className que incluye form-check-input (para switches)
    const isCheckboxOrSwitch = type === 'checkbox' ||
      type === 'switch' ||
      (e.target.className && e.target.className.includes('form-check-input'));

    // Para campos específicos que sabemos que son booleanos
    const booleanFields = ['nuevo', 'reimpresion', 'troquel_n', 'troquel_e'];
    const isBooleanField = booleanFields.includes(name);

    // Convertir a mayúscula si es el campo nombre_producto
    let newValue;
    if (name === 'nombre_producto') {
      newValue = value.toUpperCase();
    } else {
      // Si es un campo booleano o un checkbox/switch, usamos el valor checked convertido a boolean
      newValue = isBooleanField || isCheckboxOrSwitch ? Boolean(checked) : value;
    }

    setForm(prevState => {
      // Solo marcar como sucio si el valor realmente cambió
      if (prevState[name] !== newValue) {
        // Usar setTimeout para asegurar que el estado se actualice antes de marcar como sucio
        setTimeout(() => markFieldAsDirty(name), 0);
      }
      
      const newState = {
        ...prevState,
        [name]: newValue
      };
      
      // Si se cambió el tipo de solicitud, verificar si debe limpiar numero_reproceso
      if (name === 'solicitud') {
        const tipoSolicitud = tiposSolicitud.find(t => t.id === newValue);
        if (!tipoSolicitud || !tipoSolicitud.campo_orden) {
          // Si el tipo de solicitud no usa campo_orden, limpiar numero_reproceso
          newState.numero_reproceso = "";
        }
      }
      
      return newState;
    });
  };

  // Para agregar un tipo existente a la selección de la orden
  const handleAgregarExistente = (recubrimientoId) => {
    const tipoId = agregarExistente[recubrimientoId];
    if (!tipoId) return;
    const tipoObj = recubrimientoTiposGlobal.find(rt => rt.id === tipoId);
    if (!tipoObj) return;
    setRecubrimientoSeleccion(prev => ({
      ...prev,
      [recubrimientoId]: [...(prev[recubrimientoId] || []), tipoObj]
    }));
    setAgregarExistente(prev => ({ ...prev, [recubrimientoId]: "" }));
  };

  // Para crear un tipo nuevo y agregarlo a la selección de la orden (GUARDAR EN MAYÚSCULA)
  const handleAgregarNuevoTipo = async (recubrimientoId) => {
    let tipo = (nuevoTipo[recubrimientoId] || "").trim();
    if (!tipo) return;
    tipo = tipo.toUpperCase();
    const record = await pb.collection('recubrimiento_tipo_recubrimiento').create({
      recubrimiento: recubrimientoId,
      tipo
    });
    setRecubrimientoTiposGlobal(prev => [...prev, record]);
    setRecubrimientoSeleccion(prev => ({
      ...prev,
      [recubrimientoId]: [...(prev[recubrimientoId] || []), record]
    }));
    setNuevoTipo(prev => ({ ...prev, [recubrimientoId]: "" }));
  };

  // Para eliminar un tipo de la selección de la orden (no del sistema)
  const handleEliminarTipo = (recubrimientoId, tipoId) => {
    setRecubrimientoSeleccion(prev => ({
      ...prev,
      [recubrimientoId]: (prev[recubrimientoId] || []).filter(rt => rt.id !== tipoId)
    }));
  };

  // Handler para cambio de input de tipo nuevo
  const handleNuevoTipoChange = (recubrimientoId, value) => {
    setNuevoTipo(prev => ({ ...prev, [recubrimientoId]: value }));
  };

  // Handler para cambio de select de tipo existente
  const handleAgregarExistenteChange = (recubrimientoId, value) => {
    setAgregarExistente(prev => ({ ...prev, [recubrimientoId]: value }));
  };

  // Only needed for plastificado_interno in the first tab
  const handlePlastificadoChange = (e) => {
    const { name, value } = e.target;
    // Only handle plastificado_interno since external plastificado has been removed
    if (name === "plastificado_interno") {
      setPlastificadoForm(prevState => {
        // Solo marcar como sucio si el valor realmente cambió
        if (prevState[name] !== value) {
          setTimeout(() => markFieldAsDirty(`plastificado_${name}`), 0);
        }
        
        return {
          ...prevState,
          [name]: value
        };
      });
    }
  };

  // Only needed for brillo_interno in the first tab
  const handleBrilloChange = (e) => {
    const { name, value, type, checked } = e.target;
    // Only handle brillo_interno since external brillo has been removed
    if (name === "brillo_interno") {
      setBrilloForm(prev => {
        // Solo marcar como sucio si el valor realmente cambió
        if (prev[name] !== value) {
          setTimeout(() => markFieldAsDirty(`brillo_${name}`), 0);
        }
        
        return {
          ...prev,
          [name]: value
        };
      });
    }
  };

  const handlePlaneacionChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    ordenLogger.debug(`Cambiando campo de planeación: ${name}`, { value, type });
    
    // For planeacion_tamano field, ensure it's properly handled as a relation ID
    if (name === 'planeacion_tamano') {
      ordenLogger.debug('Configurando planeacion_tamano', { value, valueType: typeof value, medidasCount: medidas.length });
      
      if (value) {
        const selectedMedida = medidas.find(m => m.id === value);
        ordenLogger.debug('Medida seleccionada', { medida: selectedMedida });
      }
    }
    
    // Actualizar el form de planeación
    setPlaneacionForm(prev => {
      const newValue = type === 'checkbox' || type === 'switch' ? checked : value;
      
      // Solo marcar como sucio si el valor realmente cambió
      if (prev[name] !== newValue) {
        setTimeout(() => markFieldAsDirty(`planeacion_${name}`), 0);
      }
      
      return {
        ...prev,
        [name]: newValue
      };
    });
    
    // Si el campo que cambió es planeacion_tamano, actualizar tamano_corte en todos los parámetros
    // de corte_conversion y tamano en todos los parámetros de impresion
    if (name === 'planeacion_tamano' && value) {
      ordenLogger.debug("Propagando cambio de tamaño", { value });
      
      // Buscar el nombre de la medida seleccionada para mostrarlo
      const medidaSeleccionada = medidas.find(m => m.id === value);
      const medidaNombre = medidaSeleccionada ? medidaSeleccionada.nombre : value;
      ordenLogger.debug("Medida procesada", { medida: medidaSeleccionada?.nombre, id: medidaSeleccionada?.id });
      
      // Usar una única actualización del estado principal para evitar múltiples re-renders
      setForm(prevForm => {
        // Clonar profundamente el formulario actual
        const newForm = { ...prevForm };
        
        // Actualizar corte_conversion directamente en el form principal
        if (Array.isArray(prevForm.corte_conversion)) {
          newForm.corte_conversion = prevForm.corte_conversion.map(corte => ({
            ...corte,
            parametros: (corte.parametros || []).map(param => ({
              ...param,
              tamano_corte: medidaNombre
            }))
          }));
        }
        
        // Actualizar impresion directamente en el form principal
        // impresion aquí es la relación corte -> parametro -> impresion_tabla
        if (Array.isArray(prevForm.corte_conversion)) {
          // Recorrer cada corte_conversion para actualizar los datos de impresion dentro de los parámetros
          newForm.corte_conversion = prevForm.corte_conversion.map(corte => ({
            ...corte,
            parametros: (corte.parametros || []).map(param => {
              // Actualizar el parámetro con el nuevo tamaño de corte
              const updatedParam = {
                ...param,
                tamano_corte: medidaNombre // Nombre legible para tamano_corte
              };
              
              // Si el parámetro tiene datos de impresión, actualizamos también el tamaño allí
              if (param.impresion) {
                updatedParam.impresion = {
                  ...param.impresion,
                  tamano: value // ID de la medida para impresion_tabla
                };
              }
              
              return updatedParam;
            })
          }));
        }
        
        return newForm;
      });
      
      // Actualizar también los estados locales para mantener consistencia
      // pero de manera asíncrona para evitar ciclos de actualización
      if (Array.isArray(corteForm)) {
        const updatedCorteForm = corteForm.map(corte => ({
          ...corte,
          parametros: (corte.parametros || []).map(param => ({
            ...param,
            tamano_corte: medidaNombre
          }))
        }));
        
        // Usar setTimeout para romper el ciclo de actualización sincrónico
        setTimeout(() => {
          setCorteForm(updatedCorteForm);
        }, 0);
      }
      
      if (Array.isArray(impresionForm)) {
        const updatedImpresionForm = impresionForm.map(impresion => ({
          ...impresion,
          parametros: (impresion.parametros || []).map(param => {
            // Para el tamano en impresion, debemos usar el ID de la medida, no el nombre
            let tamanoId = value; // Este ya es el ID de la medida seleccionada en planeacion_tamano
            return {
              ...param,
              tamano: tamanoId  // Usar directamente el ID de la medida
            };
          })
        }));
        
        setTimeout(() => {
          setImpresionForm(updatedImpresionForm);
        }, 0);
      }
    }
  };
  
  // Handler para corte_conversion (actualizado para la nueva estructura donde impresion está dentro de parametro)
  const handleCorteConversionChange = React.useCallback((e) => {
    const { name, value, removedCorteIndex, removedParamIndex } = e.target;
    if (name === "corte_conversion" && Array.isArray(value)) {
      ordenLogger.debug("Actualizando corte_conversion", { cortesCount: value.length, removedCorteIndex, removedParamIndex });
      
      // Verificar que todos los parametros tienen un objeto impresion
      value.forEach((corte, corteIndex) => {
        if (corte.parametros && Array.isArray(corte.parametros)) {
          corte.parametros.forEach((parametro, paramIndex) => {
            // Garantizar que cada parámetro tiene su objeto impresion con los datos correctos
            if (!parametro.impresion) {
              ordenLogger.warn("Creando objeto impresión faltante", { corteIndex, paramIndex });
              parametro.impresion = {
                impresos: parametro.cantidad || "",
                sobrantes: parametro.sobrante || "",
                total: parametro.total || "",
                tamano: planeacionForm.planeacion_tamano || "",
                pasadas: "",
                tinta_color: "",
                montaje: ""
              };
            }
          });
        }
      });
      
      // Crear una copia profunda de los datos para trabajar con ellos
      const newCorteConversion = JSON.parse(JSON.stringify(value));
      
      // Solo marcar como sucio si realmente hay cambios en la estructura o contenido
      const hasChanges = JSON.stringify(corteForm) !== JSON.stringify(value);
      if (hasChanges) {
        setTimeout(() => markFieldAsDirty('corte_conversion'), 0);
      }
      
      // Obtener la versión actual de impresionForm para comparar y actualizar
      const currentImpresionForm = [...impresionForm];
      
      // Si se eliminó un corte o un parámetro, necesitamos asegurarnos de que impresionForm refleje ese cambio
      if (removedCorteIndex !== undefined || removedParamIndex !== undefined) {
        // Manejar cambios en la estructura cuando se eliminan elementos
        ordenLogger.debug("Elemento eliminado, actualizando estructuras relacionadas");
        
        if (removedCorteIndex !== undefined) {
          // Si se eliminó un corte completo, eliminar la entrada correspondiente en impresionForm
          currentImpresionForm.splice(removedCorteIndex, 1);
        } else if (removedParamIndex !== undefined && removedCorteIndex !== undefined) {
          // Si se eliminó un parámetro específico, eliminar la entrada correspondiente en impresionForm
          if (currentImpresionForm[removedCorteIndex] && 
              currentImpresionForm[removedCorteIndex].parametros && 
              currentImpresionForm[removedCorteIndex].parametros.length > removedParamIndex) {
            currentImpresionForm[removedCorteIndex].parametros.splice(removedParamIndex, 1);
          }
        }
      }
      
      // Asegurarnos que cada corte tiene una entrada correspondiente en impresionForm
      while (currentImpresionForm.length < newCorteConversion.length) {
        currentImpresionForm.push({
          tipo_papel: "",
          parametros: [],
          isOpen: true
        });
      }
      
      // Inicializar objetos de impresión para nuevos parámetros
      newCorteConversion.forEach((corte, corteIndex) => {
        // Asegurarnos que el papel no sea null o undefined
        if (!corte.papel) {
          corte.papel = "";
        }
        
        if (!corte.parametros) {
          corte.parametros = [];
        }
        
        // Asegurarnos que el tipo_papel en impresionForm coincide con el papel en corte
        if (currentImpresionForm[corteIndex]) {
          currentImpresionForm[corteIndex].tipo_papel = corte.papel;
          
          // Asegurarnos que hay suficientes parámetros en impresionForm
          while (currentImpresionForm[corteIndex].parametros.length < corte.parametros.length) {
            currentImpresionForm[corteIndex].parametros.push({
              impresos: "",
              sobrantes: "",
              total: "",
              tamano: planeacionForm.planeacion_tamano || "",
              pasadas: "",
              tinta_color: "",
              montaje: ""
            });
          }
        }
        
        corte.parametros.forEach((parametro, paramIndex) => {
          // Siempre asegurarnos de que existe un objeto impresión para cada parámetro
          // Si no existe, lo creamos con los valores del parámetro
          if (!parametro.impresion) {
            ordenLogger.debug("Creando objeto impresión faltante", { corteIndex, paramIndex });
            parametro.impresion = {
              impresos: parametro.cantidad || "",
              sobrantes: parametro.sobrante || "",
              total: parametro.total || "",
              // Usamos el ID de la medida seleccionada en planeación, si está disponible
              tamano: planeacionForm.planeacion_tamano || "",
              pasadas: "",
              tinta_color: "",
              montaje: ""
            };
          }
          
          
          // Actualizar también los datos en impresionForm para mantener la sincronización
          if (currentImpresionForm[corteIndex] && currentImpresionForm[corteIndex].parametros[paramIndex]) {
            const impresionParam = currentImpresionForm[corteIndex].parametros[paramIndex];
            // Copiar datos de parametro.impresion a impresionForm
            impresionParam.impresos = parametro.impresion.impresos;
            impresionParam.sobrantes = parametro.impresion.sobrantes;
            impresionParam.total = parametro.impresion.total;
            impresionParam.tamano = parametro.impresion.tamano;
            impresionParam.pasadas = parametro.impresion.pasadas;
            impresionParam.tinta_color = parametro.impresion.tinta_color;
            impresionParam.montaje = parametro.impresion.montaje;
          }
        });
      });
      
      // Extraemos y formateamos los datos de impresión para enviar al componente de Impresion
      // Este formato debe coincidir con lo que espera el componente Impresion
      const impresionData = newCorteConversion.map(corte => {
        // Asegurarnos de que cada corte tiene valores válidos
        if (!corte.parametros) {
          corte.parametros = [];
        }
        
        return {
          tipo_papel: corte.papel || "", // El tipo de papel viene de corte_conversion (asegurarnos que no sea null)
          parametros: corte.parametros.map(parametro => {
            // Si el parámetro no tiene un objeto impresion, asegurarnos de que tenga uno
            if (!parametro.impresion) {
              parametro.impresion = {
                impresos: parametro.cantidad || "",
                sobrantes: parametro.sobrante || "",
                total: parametro.total || "",
                tamano: planeacionForm.planeacion_tamano || "",
                pasadas: "",
                tinta_color: "",
                montaje: ""
              };
            }
            
            // Si tenemos datos de impresión específicos, usarlos
            if (parametro.impresion) {
              return {
                id: parametro.impresion.id, // Importante: preservar el ID para actualizar el registro correcto
                impresos: parametro.impresion.impresos || "",
                sobrantes: parametro.impresion.sobrantes || "",
                total: parametro.impresion.total || "",
                tamano: parametro.impresion.tamano || "",
                pasadas: parametro.impresion.pasadas || "",
                tinta_color: parametro.impresion.tinta_color || "",
                montaje: parametro.impresion.montaje || ""
              };
            } else {
              // Si no hay datos de impresión específicos, usar los datos del parámetro
              return {
                impresos: parametro.cantidad || "",
                sobrantes: parametro.sobrante || "",
                total: parametro.total || "",
                tamano: "",
                pasadas: "",
                tinta_color: "",
                montaje: ""
              };
            }
          }),
          isOpen: corte.isOpen || true
        };
      });
      
      ordenLogger.debug("Datos de impresión generados para componente", { count: impresionData.length });
      
      // Actualizar ambos estados: corteForm e impresionForm
      setCorteForm(newCorteConversion);
      setImpresionForm(impresionData);
      
      // Actualizar el formulario principal con la estructura completa de corte_conversion
      setForm(prev => ({
        ...prev,
        corte_conversion: newCorteConversion
      }));
    }
  }, [planeacionForm.planeacion_tamano]);
  
  // Handler para impresion (actualizado para la nueva estructura con relación entre corte_conversion, parametro e impresion_tabla)
  const handleImpresionChange = (e) => {
    const { name, value } = e.target;
    if (name === "impresion_data" && Array.isArray(value)) {
      ordenLogger.debug("Datos de impresión recibidos desde componente", { count: value.length });
      
      // Guardamos los datos de impresión en el estado local para la UI
      setImpresionForm(value);
      
      // Solo marcar como sucio si realmente hay cambios en los datos de impresión
      const hasChanges = JSON.stringify(impresionForm) !== JSON.stringify(value);
      if (hasChanges) {
        setTimeout(() => markFieldAsDirty('impresion_data'), 0);
      }
      
      // También actualizamos directamente el estado corteForm para mantener la sincronización inmediata
      const updatedCorteForm = [...corteForm];
      
      // Sincronizamos estos datos con la estructura de corte_conversion en el form principal
      setForm(prev => {
        // Verificar si tenemos datos de corte_conversion
        if (!prev.corte_conversion || !Array.isArray(prev.corte_conversion) || prev.corte_conversion.length === 0) {
          ordenLogger.warn("No hay datos de corte_conversion para sincronizar");
          return prev;
        }
        
        // Crear una copia profunda de corte_conversion para poder modificarla
        const corte_conversion = JSON.parse(JSON.stringify(prev.corte_conversion));
        
        // Asegurarnos que corte_conversion tenga al menos tantos elementos como value
        while (corte_conversion.length < value.length) {
          corte_conversion.push({
            papel: "",
            parametros: [{
              cantidad: "",
              sobrante: "",
              total: "",
              tamano_corte: "",
              tamano_cortado: "",
              detalles: "",
              impresion: {
                impresos: "",
                sobrantes: "",
                total: "",
                tamano: planeacionForm.planeacion_tamano || "",
                pasadas: "",
                tinta_color: "",
                montaje: ""
              }
            }],
            isOpen: true
          });
        }
        
        // Iterar sobre los datos de impresión y buscar los cortes correspondientes por tipo de papel
        value.forEach((impresion, impIndex) => {
          // Asegurarnos que el tipo_papel existe
          if (!impresion.tipo_papel && corte_conversion[impIndex]) {
            // Si no hay tipo_papel en impresion pero sí hay datos en corte_conversion,
            // tomamos el papel desde corte_conversion
            impresion.tipo_papel = corte_conversion[impIndex].papel;
          } else if (impresion.tipo_papel && corte_conversion[impIndex] && !corte_conversion[impIndex].papel) {
            // Si hay tipo_papel en impresion pero no en corte_conversion, 
            // actualizamos el papel en corte_conversion
            corte_conversion[impIndex].papel = impresion.tipo_papel;
          }
          
          // Encontrar el índice del corte que corresponde a este tipo de papel
          const corteIndex = corte_conversion.findIndex(corte => 
            corte.papel === impresion.tipo_papel
          );
          
          if (corteIndex !== -1) {
            // Si encontramos un corte correspondiente, actualizamos sus parámetros
            impresion.parametros.forEach((paramImpresion, paramIndex) => {
              // Verificar si existe el parámetro correspondiente en corte_conversion
              if (!corte_conversion[corteIndex].parametros) {
                corte_conversion[corteIndex].parametros = [];
              }
              
              // Asegurarnos que hay suficientes parámetros en el corte
              while (corte_conversion[corteIndex].parametros.length <= paramIndex) {
                corte_conversion[corteIndex].parametros.push({
                  cantidad: "",
                  sobrante: "",
                  total: "",
                  tamano_corte: "",
                  tamano_cortado: "",
                  detalles: "",
                  impresion: {
                    impresos: "",
                    sobrantes: "",
                    total: "",
                    tamano: planeacionForm.planeacion_tamano || "",
                    pasadas: "",
                    tinta_color: "",
                    montaje: ""
                  }
                });
              }
              
              // Si el objeto impresion no existe en el parámetro, inicializarlo
              if (!corte_conversion[corteIndex].parametros[paramIndex].impresion) {
                corte_conversion[corteIndex].parametros[paramIndex].impresion = {};
              }
              
              // Actualizar el objeto impresion dentro del parámetro de corte
              // (esto corresponde a los datos de impresion_tabla relacionados con el parámetro)
              // Asegurarse de preservar el ID si existe (importante para ediciones)
              const currentImpressionId = corte_conversion[corteIndex].parametros[paramIndex].impresion?.id || null;
              
              corte_conversion[corteIndex].parametros[paramIndex].impresion = {
                ...(currentImpressionId ? { id: currentImpressionId } : {}), // Preservar ID si existe
                impresos: paramImpresion.impresos || "",
                sobrantes: paramImpresion.sobrantes || "",
                total: paramImpresion.total || "",
                tamano: paramImpresion.tamano || "",
                pasadas: paramImpresion.pasadas || "",
                tinta_color: paramImpresion.tinta_color || "",
                montaje: paramImpresion.montaje || ""
              };
              
              
              // Actualizar también en el estado corteForm para sincronización inmediata
              if (updatedCorteForm[corteIndex] && updatedCorteForm[corteIndex].parametros[paramIndex]) {
                updatedCorteForm[corteIndex].parametros[paramIndex].impresion = {
                  ...(currentImpressionId ? { id: currentImpressionId } : {}), // Preservar ID si existe
                  impresos: paramImpresion.impresos || "",
                  sobrantes: paramImpresion.sobrantes || "",
                  total: paramImpresion.total || "",
                  tamano: paramImpresion.tamano || "",
                  pasadas: paramImpresion.pasadas || "",
                  tinta_color: paramImpresion.tinta_color || "",
                  montaje: paramImpresion.montaje || ""
                };
              }
            });
          } else if (impresion.tipo_papel) {
            // Si el tipo de papel existe en impresion pero no encontramos un corte correspondiente,
            // creamos uno nuevo en corte_conversion
            corte_conversion.push({
              papel: impresion.tipo_papel,
              parametros: impresion.parametros.map(param => ({
                cantidad: "",
                sobrante: "",
                total: "",
                tamano_corte: "",
                tamano_cortado: "",
                detalles: "",
                impresion: {
                  impresos: param.impresos || "",
                  sobrantes: param.sobrantes || "",
                  total: param.total || "",
                  tamano: param.tamano || "",
                  pasadas: param.pasadas || "",
                  tinta_color: param.tinta_color || "",
                  montaje: param.montaje || ""
                }
              })),
              isOpen: true
            });
            ordenLogger.debug("Creado nuevo corte", { tipoPapel: impresion.tipo_papel });
          } else {
            ordenLogger.warn("No se encontró corte correspondiente", { tipoPapel: impresion.tipo_papel });
          }
        });
        
        ordenLogger.debug("corte_conversion actualizado con datos de impresión", { cortesCount: corte_conversion.length });
        
        // Actualizar también el estado de corteForm para mantener todo sincronizado
        setCorteForm(updatedCorteForm);
        
        // Devolver el formulario actualizado con los nuevos datos de corte_conversion
        return {
          ...prev,
          corte_conversion: corte_conversion
        };
      });
    }
  };

  // Handler for brillo_interno checkboxes
  const handleBrilloInternoChange = (subtipoId) => {
    setBrilloForm(prevState => {
      // Asegurarnos que brillo_interno es un array
      const currentBrilloInterno = Array.isArray(prevState.brillo_interno) ? prevState.brillo_interno : [];

      // Si ya está seleccionado, lo eliminamos; de lo contrario, lo agregamos
      const updatedBrilloInterno = currentBrilloInterno.includes(subtipoId)
        ? currentBrilloInterno.filter(id => id !== subtipoId)
        : [...currentBrilloInterno, subtipoId];

      return { ...prevState, brillo_interno: updatedBrilloInterno };
    });
  };

  // Handler for plastificado_interno checkboxes
  const handlePlastificadoInternoChange = (subtipoId) => {
    setPlastificadoForm(prevState => {
      // Asegurarnos que plastificado_interno es un array
      const currentPlastificadoInterno = Array.isArray(prevState.plastificado_interno) ? prevState.plastificado_interno : [];

      // Si ya está seleccionado, lo eliminamos; de lo contrario, lo agregamos
      const updatedPlastificadoInterno = currentPlastificadoInterno.includes(subtipoId)
        ? currentPlastificadoInterno.filter(id => id !== subtipoId)
        : [...currentPlastificadoInterno, subtipoId];

      return { ...prevState, plastificado_interno: updatedPlastificadoInterno };
    });
  };

  // Handler for pegue_interno checkboxes
  const handlePegueInternoChange = (subtipoId) => {
    setPegueForm(prevState => {
      // Asegurarnos que pegue_interno es un array
      const currentPegueInterno = Array.isArray(prevState.pegue_interno) ? prevState.pegue_interno : [];

      // Si ya está seleccionado, lo eliminamos; de lo contrario, lo agregamos
      const updatedPegueInterno = currentPegueInterno.includes(subtipoId)
        ? currentPegueInterno.filter(id => id !== subtipoId)
        : [...currentPegueInterno, subtipoId];

      return { ...prevState, pegue_interno: updatedPegueInterno };
    });
  };

  // Handler for troquelado_interno checkboxes
  const handleTroqueladoInternoChange = (subtipoId) => {
    setTroqueladoForm(prevState => {
      // Asegurarnos que troquelado_interno es un array
      const currentTroqueladoInterno = Array.isArray(prevState.troquelado_interno) ? prevState.troquelado_interno : [];
      
      // Si ya está seleccionado, lo eliminamos; de lo contrario, lo agregamos
      const updatedTroqueladoInterno = currentTroqueladoInterno.includes(subtipoId)
        ? currentTroqueladoInterno.filter(id => id !== subtipoId)
        : [...currentTroqueladoInterno, subtipoId];
        
      return { ...prevState, troquelado_interno: updatedTroqueladoInterno };
    });
  };
  
  const handleAcabadoInternoChange = (subtipoId) => {
    setAcabadoForm(prevState => {
      // Asegurarnos que acabado_interno es un array
      const currentAcabadoInterno = Array.isArray(prevState.acabado_interno) ? prevState.acabado_interno : [];
      
      // Si ya está seleccionado, lo eliminamos; de lo contrario, lo agregamos
      const updatedAcabadoInterno = currentAcabadoInterno.includes(subtipoId)
        ? currentAcabadoInterno.filter(id => id !== subtipoId)
        : [...currentAcabadoInterno, subtipoId];
        
      return { ...prevState, acabado_interno: updatedAcabadoInterno };
    });
  };  // Manejar errores de validación de componentes hijos
  const handleValidationError = React.useCallback((error) => {
    // Si el mensaje es null, eliminamos el error para ese componente
    if (error.message === null) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[error.component];
        return newErrors;
      });
    } else {
      // Si hay un mensaje de error, lo añadimos
      setValidationErrors(prev => ({ ...prev, [error.component]: error.message }));
    }
  }, []);

  // Guardar NUEVA orden
  // Función para obtener y actualizar el número de secuencia
  async function getNextSequenceNumber() {
    let retries = 3;
    while (retries > 0) {
      try {
        // Obtener la última secuencia
        let sequence = await pb.collection('orden_sequence').getFirstListItem('');
        if (!sequence) {
          // Si no existe, crear con valor inicial
          sequence = await pb.collection('orden_sequence').create({ sequence: 1 });
        }
        return sequence.sequence + 1;
      } catch (err) {
        retries--;
        if (retries === 0) throw new Error('No se pudo obtener el número de secuencia');
        // Esperar un momento antes de reintentar
        await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }
  }

  const handleSave = async (e = null) => {
    if (e) e.preventDefault();
    if (form.fecha_entrega && form.fecha_inicio && form.fecha_entrega < form.fecha_inicio) {
      setModalError("La fecha de entrega no puede ser menor a la fecha de inicio.");
      return;
    }
    
    // Verificar errores de validación de componentes
    if (Object.keys(validationErrors).length > 0) {
      const errorMessages = Object.values(validationErrors).join(', ');
      setModalError(`Por favor corrija los siguientes errores: ${errorMessages}`);
      return;
    }
    
    setSaving(true);
    setModalError("");

    let nextSequence;
    try {
      nextSequence = await getNextSequenceNumber();
      const recubrimientoIds = [];
      Object.values(recubrimientoSeleccion).forEach(arr => {
        arr.forEach(rtr => recubrimientoIds.push(rtr.id));
      });

      // Crear registro de planeación
      let planeacionId = null;
      // Asegurarnos que tamano se maneje como un ID de relación a la colección 'medida'
      ordenLogger.debug("Creando datos de planeación", { tamano: planeacionForm.planeacion_tamano });
      const planeacionData = {
        tamano: planeacionForm.planeacion_tamano || "", // RELATION_RECORD_ID - ID de relación a la colección 'medida'
        abierto_ancho: planeacionForm.planeacion_abierto_ancho || "",
        abierto_alto: planeacionForm.planeacion_abierto_alto || "",
        armado_ancho: planeacionForm.planeacion_armado_ancho || "",
        armado_alto: planeacionForm.planeacion_armado_alto || "",
        armado_prof: planeacionForm.planeacion_armado_prof || "",
        tintas: String(planeacionForm.planeacion_tintas || ""), // Explicitly convert to string
        parametros_tinta: planeacionForm.parametros_tinta || [], // Array of parametros_tinta IDs
        lineatura: planeacionForm.planeacion_lineatura || "",
        tipo_pinza: planeacionForm.planeacion_tipo_pinza || "",
        troquel_n: Boolean(planeacionForm.planeacion_troquel_n), // Cambiado de troquel_nuevo a troquel_n
        troquel_e: Boolean(planeacionForm.planeacion_troquel_e), // Cambiado de troquel_existente a troquel_e
        numero_troquel: parseInt(planeacionForm.planeacion_numero_troquel) || 0,
        cabida_preprensa: planeacionForm.planeacion_cabida_preprensa || "",
      };

      // Verificar específicamente que hay un valor válido para tamano
      if (!planeacionData.tamano) {
        ordenLogger.warn("Campo tamano en planeación vacío");
      } else {
        ordenLogger.debug("Tamano configurado en planeación", { tamano: planeacionData.tamano });
      }

      const hasPlaneacionData = Object.values(planeacionData).some(val => val);
      if (hasPlaneacionData) {
        // Debug para verificar el valor exacto
        ordenLogger.debug("Guardando datos de planeación", { 
          numeroTroquel: planeacionData.numero_troquel,
          numeroTroquelType: typeof planeacionData.numero_troquel
        });
        
        try {
          const planeacionRecord = await pb.collection('planeacion').create(planeacionData);
          planeacionId = planeacionRecord.id;
          ordenLogger.info("Planeación guardada exitosamente", { id: planeacionRecord.id });
        } catch (error) {
          ordenLogger.error("Error guardando datos de planeación", error);
          // Continue with the form submission even if planeacion save fails
        }
      }
      
      // Log de verificación para planeacion
      if (planeacionId) {
        try {
          const verifyPlaneacion = await pb.collection('planeacion').getOne(planeacionId);
          ordenLogger.debug("Planeación verificada", { id: verifyPlaneacion.id, tintas: verifyPlaneacion.tintas });
        } catch (error) {
          ordenLogger.error("Error verificando planeación", error);
        }
      }
      
      // Crear registros de corte_conversion
      const corteConversionIds = [];
      if (Array.isArray(form.corte_conversion) && form.corte_conversion.length > 0) {
        for (const corte of form.corte_conversion) {
          // Para cada corte, crear los registros de parámetros
          const parametroIds = [];
          if (Array.isArray(corte.parametros) && corte.parametros.length > 0) {
            for (const parametro of corte.parametros) {
              // Primero verificamos si ya existe un registro de impresión asociado
              let impresionId = null;
              
              // Si tenemos un ID de impresión válido, lo usamos
              if (parametro.impresion && parametro.impresion.id && !parametro.impresion.id.startsWith('temp_')) {
                impresionId = parametro.impresion.id;
                
                // Actualizar el registro de impresión existente
                const impresionData = {
                  impresos: parseInt(parametro.impresion.impresos || 0),
                  sobrantes: parseInt(parametro.impresion.sobrantes || 0),
                  total: parseInt(parametro.impresion.total || 0),
                  tamano: parametro.impresion.tamano || planeacionForm.planeacion_tamano || "",
                  pasadas: parametro.impresion.pasadas ? parseInt(parametro.impresion.pasadas) : 0,
                  tinta_color: parametro.impresion.tinta_color || "",
                  montaje: parametro.impresion.montaje || ""
                };
                
                try {
                  await pb.collection('impresion_tabla').update(impresionId, impresionData);
                  ordenLogger.info("Registro de impresión actualizado", { impresionId });
                } catch (error) {
                  ordenLogger.error("Error actualizando registro de impresión", error);
                  // Si falla la actualización, creamos uno nuevo
                  try {
                    const impresionRecord = await pb.collection('impresion_tabla').create(impresionData);
                    impresionId = impresionRecord.id;
                    ordenLogger.info("Registro de impresión creado como fallback", { impresionId });
                  } catch (innerError) {
                    ordenLogger.error("Error creando registro de impresión como fallback", innerError);
                  }
                }
              } 
              // Si no tenemos un ID válido pero sí datos de impresión, creamos un nuevo registro
              else if (parametro.impresion) {
                // Crear registro de impresión
                // Solo obtener los IDs de parametros_tinta que están realmente seleccionados en impresión
                let parametrosTintaArray = [];
                const hasTintasInImpresion = parametro.impresion?.tinta_color && 
                                            parametro.impresion.tinta_color.trim() !== '' && 
                                            parametro.impresion.tinta_color.trim() !== 'Seleccionar tinta...';
                
                if (hasTintasInImpresion) {
                  // Parsear las tintas seleccionadas en impresión manteniendo el orden
                  const tintasSeleccionadas = parametro.impresion.tinta_color.split('\n')
                    .filter(tinta => tinta.trim() !== '')
                    .map(tinta => tinta.trim());
                  
                  // Encontrar los IDs de las tintas respetando el orden de selección
                  if (parametrosTinta && parametrosTinta.length > 0) {
                    parametrosTintaArray = tintasSeleccionadas
                      .map(tintaSeleccionada => {
                        // Buscar el parámetro que corresponde a esta tinta seleccionada
                        const parametroTinta = parametrosTinta.find(p => 
                          tintaSeleccionada.includes(p.nombre)
                        );
                        return parametroTinta ? parametroTinta.id : null;
                      })
                      .filter(id => id !== null); // Filtrar IDs nulos
                  }
                }
                
                const impresionData = {
                  impresos: parametro.impresion.impresos ? parseInt(parametro.impresion.impresos) : 0,
                  sobrantes: parametro.impresion.sobrantes ? parseInt(parametro.impresion.sobrantes) : 0,
                  total: parametro.impresion.total ? parseInt(parametro.impresion.total) : 0,
                  tamano: parametro.impresion.tamano || "",
                  pasadas: parametro.impresion.pasadas ? parseInt(parametro.impresion.pasadas) : 0,
                  tinta_color: parametro.impresion.tinta_color || "",
                  montaje: parametro.impresion.montaje || "",
                  parametros_tinta: parametrosTintaArray // Solo incluir los que están realmente seleccionados
                };
                
                try {
                  const impresionRecord = await pb.collection('impresion_tabla').create(impresionData);
                  impresionId = impresionRecord.id;
                  ordenLogger.info("Registro de impresión creado", { impresionId });
                } catch (error) {
                  ordenLogger.error("Error creando registro de impresión", error);
                }
              }
              
              // Luego crear o actualizar el parámetro, incluyendo la relación con la impresión
              const parametroData = {
                cantidad: parametro.cantidad ? parseInt(parametro.cantidad) : 0,
                sobrante: parametro.sobrante ? parseInt(parametro.sobrante) : 0,
                total: parametro.total ? parseInt(parametro.total) : 0,
                tamano_corte: parametro.tamano_corte || "",
                tamano_cortado: parametro.tamano_cortado || "",
                detalles: parametro.detalles || "",
                // Agregar relación con impresion_tabla si existe
                ...(impresionId ? { impresion: impresionId } : {})
              };
              
              // Crear el registro de parámetro
              const parametroRecord = await pb.collection('corte_conversion_tabla').create(parametroData);
              parametroIds.push(parametroRecord.id);
            }
          }
          
          // Crear el registro de corte_conversion
          const corteData = {
            papel: corte.papel || "",
            parametro: parametroIds
          };
          
          const corteRecord = await pb.collection('corte_conversion').create(corteData);
          corteConversionIds.push(corteRecord.id);
        }
      }
      
      // Ya no es necesario crear registros de impresión separados como colección principal, 
      // porque ahora los creamos como parte de los parámetros de corte_conversion
      // y la relación ya está establecida en los parámetros
      const impresionIds = [];

      // Crear registro de la orden
      const orderData = {
        numero: String(nextSequence),
        cliente: form.cliente,
        asesor_comercial: form.asesor_comercial,
        nombre_producto: form.nombre_producto,
        fecha_inicio: toPBDate(form.fecha_inicio),
        fecha_entrega: toPBDate(form.fecha_entrega),
        corte_facturacion: toPBDate(form.corte_facturacion),
        facturacion: form.facturacion,
        cantidad_solicitada: form.cantidad_solicitada,
        oc: form.oc,
        numero_reproceso: form.numero_reproceso ? parseInt(form.numero_reproceso) : 0,
        trabajo_interno: Boolean(form.trabajo_interno),
        reimpresion: Boolean(form.reimpresion),
        producto: form.tipo_producto, // Corregido: producto en lugar de tipo_producto
        solicitud: form.solicitud,
        referencia_trabajo: form.referencia_trabajo,
        tipo_papel: form.tipo_papel,
        tamano_papel: form.tamano_papel,
        cabida_preprensa: form.cabida_preprensa,
        tintas: form.tintas,
        detalles: form.detalles,
        cantidad: form.cantidad,
        sobrantes: form.sobrantes,
        tamano_corte: form.tamano_corte,
        troquel_n: form.troquel_n,
        troquel_e: form.troquel_e,
        pliegos: form.pliegos,
        acabado_general: form.acabado_general,
        observaciones: form.observaciones,
        recubrimiento: recubrimientoIds,
        planeacion: planeacionId,
        // Los campos brillo_interno, plastificado_interno, pegue_interno y acabado_interno están relacionados directamente con la orden_produccion
        // No tienen relación con los registros de brillo, plastificado o pegue
        brillo_interno: brilloForm.brillo_interno,
        plastificado_interno: plastificadoForm.plastificado_interno,
        pegue_interno: pegueForm.pegue_interno,
        acabado_interno: acabadoForm.acabado_interno,
        activo: true,
      };

      // Make sure arrays are properly formatted before sending
      orderData.brillo_interno = Array.isArray(orderData.brillo_interno) ? orderData.brillo_interno : [];
      orderData.plastificado_interno = Array.isArray(orderData.plastificado_interno) ? orderData.plastificado_interno : [];
      orderData.troquelado_interno = Array.isArray(troqueladoForm.troquelado_interno) ? troqueladoForm.troquelado_interno : [];
      orderData.pegue_interno = Array.isArray(orderData.pegue_interno) ? orderData.pegue_interno : [];
      orderData.acabado_interno = Array.isArray(orderData.acabado_interno) ? orderData.acabado_interno : [];
      
      // Agregar los IDs de corte_conversion a la orden
      orderData.corte_conversion = corteConversionIds;
      
      // Agregar los IDs de impresion a la orden
      orderData.impresion = impresionIds;
      
      const createdOrden = await ordenRepository.createWithValidation(orderData);

      // Solo si la orden se creó exitosamente, actualizamos la secuencia
      let sequence = await pb.collection('orden_sequence').getFirstListItem('');
      await pb.collection('orden_sequence').update(sequence.id, { sequence: nextSequence });

      // Orden creada exitosamente - los materiales se solicitarán manualmente
      showAlert('success', 'Orden creada', 'Orden creada exitosamente. Puede solicitar materiales manualmente usando el botón "Agregar Material".');

      setModalAdd(false);
      clearDirtyFields(); // Limpiar campos sucios después de guardar exitosamente
      reloadOrdenes();
    } catch (err) {
      setModalError("Error al guardar la orden y sus datos relacionados");
    }
    setSaving(false);
  };


  // Guardar EDICIÓN de orden
  const handleUpdate = async (e = null) => {
    if (e) e.preventDefault();
    if (form.fecha_entrega && form.fecha_inicio && form.fecha_entrega < form.fecha_inicio) {
      setModalError("La fecha de entrega no puede ser menor a la fecha de inicio.");
      return;
    }
    
    // Verificar errores de validación de componentes
    if (Object.keys(validationErrors).length > 0) {
      const errorMessages = Object.values(validationErrors).join(', ');
      setModalError(`Por favor corrija los siguientes errores: ${errorMessages}`);
      return;
    }
    setSaving(true);
    setModalError("");
    try {
      const recubrimientoIds = [];
      Object.values(recubrimientoSeleccion).forEach(arr => {
        arr.forEach(rtr => recubrimientoIds.push(rtr.id));
      });

      // Get current order to check existing relations
      const orden = await ordenRepository.getById(editId);

      // Crear o actualizar registro de planeación
      let planeacionId = orden.planeacion;
      ordenLogger.debug("Preparando datos para actualizar planeación", { tamano: planeacionForm.planeacion_tamano });
      
      const planeacionData = {
        tamano: planeacionForm.planeacion_tamano || "", // RELATION_RECORD_ID - ID de relación a la colección 'medida'
        abierto_ancho: planeacionForm.planeacion_abierto_ancho || "",
        abierto_alto: planeacionForm.planeacion_abierto_alto || "",
        armado_ancho: planeacionForm.planeacion_armado_ancho || "",
        armado_alto: planeacionForm.planeacion_armado_alto || "",
        armado_prof: planeacionForm.planeacion_armado_prof || "",
        tintas: String(planeacionForm.planeacion_tintas || ""), // Explicitly convert to string
        parametros_tinta: planeacionForm.parametros_tinta || [], // Array of parametros_tinta IDs
        lineatura: planeacionForm.planeacion_lineatura || "",
        tipo_pinza: planeacionForm.planeacion_tipo_pinza || "",
        troquel_n: Boolean(planeacionForm.planeacion_troquel_n), // Cambiado de troquel_nuevo a troquel_n
        troquel_e: Boolean(planeacionForm.planeacion_troquel_e), // Cambiado de troquel_existente a troquel_e
        numero_troquel: parseInt(planeacionForm.planeacion_numero_troquel) || 0,
        cabida_preprensa: planeacionForm.planeacion_cabida_preprensa || "",
      };

      ordenLogger.debug("Datos de planeación para actualizar", planeacionData);
      // Debug para verificar los valores exactos en actualización
      // Datos de debugging eliminados - usar logger.debug si es necesario



      
      if (planeacionId) {
        try {
          const updatedPlaneacion = await pb.collection('planeacion').update(planeacionId, planeacionData);
          ordenLogger.info("Planeación actualizada exitosamente", { id: updatedPlaneacion.id });
        } catch (error) {
          ordenLogger.error("Error actualizando planeación", error);
          // Continue with form submission even if update fails
        }
      } else {
        try {
          const newPlaneacion = await pb.collection('planeacion').create(planeacionData);
          planeacionId = newPlaneacion.id;
          ordenLogger.info("Nueva planeación creada durante actualización", { id: newPlaneacion.id });
        } catch (error) {
          ordenLogger.error("Error creando nueva planeación durante actualización", error);
          // Continue with form submission even if create fails
        }
      }
      
      // Log de verificación para planeacion en update
      if (planeacionId) {
        try {
          const verifyPlaneacion = await pb.collection('planeacion').getOne(planeacionId);
          ordenLogger.debug("Planeación verificada después de actualización", { id: verifyPlaneacion.id, tintas: verifyPlaneacion.tintas });
        } catch (error) {
          ordenLogger.error("Error verificando planeación después de actualizar/crear", error);
        }
      }

      // Actualizar registro principal de la orden
      const orderData = {
        numero: form.numero,
        cliente: form.cliente,
        asesor_comercial: form.asesor_comercial,
        nombre_producto: form.nombre_producto,
        fecha_inicio: toPBDate(form.fecha_inicio),
        fecha_entrega: toPBDate(form.fecha_entrega),
        corte_facturacion: toPBDate(form.corte_facturacion),
        facturacion: form.facturacion,
        cantidad_solicitada: form.cantidad_solicitada,
        oc: form.oc,
        numero_reproceso: form.numero_reproceso ? parseInt(form.numero_reproceso) : 0,
        trabajo_interno: Boolean(form.trabajo_interno),
        reimpresion: form.reimpresion === true,
        tipo_trabajo: form.tipo_trabajo,
        producto: form.tipo_producto, // Corregido: producto en lugar de tipo_producto
        solicitud: form.solicitud,
        referencia_trabajo: form.referencia_trabajo,
        tipo_papel: form.tipo_papel,
        tamano_papel: form.tamano_papel,
        cabida_preprensa: form.cabida_preprensa,
        tintas: form.tintas,
        detalles: form.detalles,
        cantidad: form.cantidad,
        sobrantes: form.sobrantes,
        tamano_corte: form.tamano_corte,
        troquel_n: form.troquel_n,
        troquel_e: form.troquel_e,
        pliegos: form.pliegos,
        acabado_general: form.acabado_general,
        observaciones: form.observaciones,
        recubrimiento: recubrimientoIds,
        planeacion: planeacionId,
        // Los campos brillo_interno, plastificado_interno, pegue_interno y acabado_interno están relacionados directamente con la orden_produccion
        // No tienen relación con los registros de brillo, plastificado o pegue
        brillo_interno: brilloForm.brillo_interno,
        plastificado_interno: plastificadoForm.plastificado_interno,
        pegue_interno: pegueForm.pegue_interno,
        troquelado_interno: troqueladoForm.troquelado_interno,
        acabado_interno: acabadoForm.acabado_interno,
        activo: true,
      };

      // Make sure arrays are properly formatted before sending
      orderData.brillo_interno = Array.isArray(orderData.brillo_interno) ? orderData.brillo_interno : [];
      orderData.plastificado_interno = Array.isArray(orderData.plastificado_interno) ? orderData.plastificado_interno : [];
      orderData.troquelado_interno = Array.isArray(troqueladoForm.troquelado_interno) ? troqueladoForm.troquelado_interno : [];
      orderData.pegue_interno = Array.isArray(orderData.pegue_interno) ? orderData.pegue_interno : [];
      orderData.acabado_interno = Array.isArray(orderData.acabado_interno) ? orderData.acabado_interno : [];
      
      // Manejar la actualización de corte_conversion
      // Primero obtenemos la orden actual para comparar los IDs existentes
      const ordenActual = await ordenRepository.getById(editId);
      const corteConversionActualIds = ordenActual.corte_conversion || [];
      
      // Crear nuevos registros de corte_conversion
      const corteConversionIds = [];
      if (Array.isArray(form.corte_conversion) && form.corte_conversion.length > 0) {
        ordenLogger.operation("Actualizando cortes de conversión", { totalCortes: form.corte_conversion.length });
        for (const corte of form.corte_conversion) {
          ordenLogger.debug("Procesando corte individual", { 
            id: corte.id, 
            hasId: !!corte.id, 
            startsWithTemp: corte.id?.startsWith('temp_') 
          });
          // Para cada corte, crear o actualizar los registros de parámetros
          const parametroIds = [];
          
          if (Array.isArray(corte.parametros) && corte.parametros.length > 0) {
            for (const parametro of corte.parametros) {
              // Verificar si este parámetro ya tiene un registro existente en la base de datos
              let existingParametro = null;
              if (parametro.id && !parametro.id.startsWith('param_') && !parametro.id.startsWith('temp_')) {
                try {
                  // Intentar obtener el parámetro existente para ver si ya tiene un ID de impresión
                  existingParametro = await pb.collection('corte_conversion_tabla').getOne(parametro.id);
                  ordenLogger.debug("Parámetro existente encontrado", { id: existingParametro.id, impresion: existingParametro.impresion || "ninguno" });
                } catch (err) {
                  ordenLogger.error("Error obteniendo parámetro existente", err);
                }
              }
              
              // 1. Primero crear o actualizar el registro de impresion_tabla
              let impresionId = existingParametro?.impresion;
              
              // Siempre debemos tener un registro de impresión para cada parámetro
              // Usar los valores específicos de impresión si existen, si no, usar los valores del parámetro
              // Solo obtener los IDs de parametros_tinta que están realmente seleccionados en impresión
              let parametrosTintaIds = [];
              const hasTintasInImpresion = parametro.impresion?.tinta_color && 
                                          parametro.impresion.tinta_color.trim() !== '' && 
                                          parametro.impresion.tinta_color.trim() !== 'Seleccionar tinta...';
              
              if (hasTintasInImpresion) {
                // Parsear las tintas seleccionadas en impresión manteniendo el orden
                const tintasSeleccionadas = parametro.impresion.tinta_color.split('\n')
                  .filter(tinta => tinta.trim() !== '')
                  .map(tinta => tinta.trim());
                
                // Encontrar los IDs de las tintas respetando el orden de selección
                if (parametrosTinta && parametrosTinta.length > 0) {
                  parametrosTintaIds = tintasSeleccionadas
                    .map(tintaSeleccionada => {
                      // Buscar el parámetro que corresponde a esta tinta seleccionada
                      const parametroTinta = parametrosTinta.find(p => 
                        tintaSeleccionada.includes(p.nombre)
                      );
                      return parametroTinta ? parametroTinta.id : null;
                    })
                    .filter(id => id !== null); // Filtrar IDs nulos
                }
              }
              // Si no hay tintas en impresión, parametrosTintaIds permanece como array vacío

              const impresionData = {
                impresos: parseInt(parametro.impresion?.impresos || 0),
                sobrantes: parseInt(parametro.impresion?.sobrantes || 0),
                total: parseInt(parametro.impresion?.total || 0),
                tamano: parametro.impresion?.tamano || planeacionForm.planeacion_tamano || "",
                pasadas: parametro.impresion?.pasadas ? parseInt(parametro.impresion.pasadas) : 0,
                tinta_color: parametro.impresion?.tinta_color || "",
                montaje: parametro.impresion?.montaje || "",
                parametros_tinta: parametrosTintaIds // Siempre incluir, incluso si está vacío
              };
              
              // Prioridad 1: Usar el ID de impresión que ya existe en la base de datos para este parámetro
              if (impresionId) {
                ordenLogger.info("Actualizando impresión existente", { impresionId });
                try {
                  await pb.collection('impresion_tabla').update(impresionId, impresionData);
                } catch (err) {
                  ordenLogger.error("Error actualizando impresion_tabla", { impresionId, error: err });
                  // Si falla la actualización, crear uno nuevo
                  const newImpresion = await pb.collection('impresion_tabla').create(impresionData);
                  impresionId = newImpresion.id;
                  ordenLogger.info("Nueva impresión creada tras fallo", { impresionId });
                }
              }
              // Prioridad 2: Usar el ID de impresión proporcionado en el objeto de parámetro actual
              else if (parametro.impresion?.id && !parametro.impresion.id.startsWith('temp_')) {
                try {
                  ordenLogger.info("Actualizando impresión desde parámetro", { impresionId: parametro.impresion.id });
                  await pb.collection('impresion_tabla').update(parametro.impresion.id, impresionData);
                  impresionId = parametro.impresion.id;
                } catch (err) {
                  ordenLogger.error("Error actualizando impresion_tabla desde parámetro", { impresionId: parametro.impresion.id, error: err });
                  // Si falla la actualización, crear uno nuevo
                  const newImpresion = await pb.collection('impresion_tabla').create(impresionData);
                  impresionId = newImpresion.id;
                  ordenLogger.info("Nueva impresión creada tras fallo", { impresionId });
                }
              }
              // Prioridad 3: Crear un nuevo registro de impresión
              else {
                ordenLogger.debug("Creando nuevo registro de impresión");
                const newImpresion = await pb.collection('impresion_tabla').create(impresionData);
                impresionId = newImpresion.id;
                ordenLogger.info("Nueva impresión creada", { impresionId });
              }

              // 2. Luego crear o actualizar el registro de parámetro
              const parametroData = {
                cantidad: parametro.cantidad ? parseInt(parametro.cantidad) : 0,
                sobrante: parametro.sobrante ? parseInt(parametro.sobrante) : 0,
                total: parametro.total ? parseInt(parametro.total) : 0,
                tamano_corte: parametro.tamano_corte || "",
                tamano_cortado: parametro.tamano_cortado || "",
                detalles: parametro.detalles || "",
                impresion: impresionId // Relación con impresion_tabla - esto es crítico
              };
              
              ordenLogger.debug("Datos de parámetro preparados", parametroData);
              
              let parametroId;
              // 3. Crear o actualizar el registro de corte_conversion_tabla
              if (parametro.id && !parametro.id.startsWith('param_') && !parametro.id.startsWith('temp_')) {
                try {
                  ordenLogger.info("Actualizando parámetro", { parametroId: parametro.id, impresionId });
                  const updatedParam = await pb.collection('corte_conversion_tabla').update(parametro.id, parametroData);
                  parametroId = parametro.id;
                  ordenLogger.debug("Parámetro actualizado exitosamente", { parametroId: updatedParam.id });
                } catch (err) {
                  ordenLogger.error("Error actualizando corte_conversion_tabla", { parametroId: parametro.id, error: err });
                  // Si falla la actualización, crear uno nuevo
                  const parametroRecord = await pb.collection('corte_conversion_tabla').create(parametroData);
                  parametroId = parametroRecord.id;
                  ordenLogger.info("Nuevo parámetro creado tras fallo", { parametroId });
                }
              } else {
                ordenLogger.debug("Creando nuevo registro de parámetro");
                const parametroRecord = await pb.collection('corte_conversion_tabla').create(parametroData);
                parametroId = parametroRecord.id;
                ordenLogger.info("Nuevo parámetro creado", { parametroId });
              }
              
              parametroIds.push(parametroId);
            }
          }
          
          // Preparar datos del corte
          const corteData = {
            papel: corte.papel || "",
            parametro: parametroIds
          };
          
          let corteId;
          // Determinar si es un registro existente o nuevo
          const isExistingRecord = corte.id && 
                                  typeof corte.id === 'string' && 
                                  !corte.id.startsWith('temp_') && 
                                  corteConversionActualIds.includes(corte.id);
          
          if (isExistingRecord) {
            ordenLogger.info("Actualizando corte existente", { corteId: corte.id });
            await pb.collection('corte_conversion').update(corte.id, corteData);
            corteId = corte.id;
          } else {
            ordenLogger.info("Creando nuevo corte", { hasId: !!corte.id });
            const corteRecord = await pb.collection('corte_conversion').create(corteData);
            corteId = corteRecord.id;
          }
          
          corteConversionIds.push(corteId);
        }
      }
      
      // Eliminar registros de corte_conversion que ya no están en la orden actualizada
      const cortesAEliminar = corteConversionActualIds.filter(id => !corteConversionIds.includes(id));
      for (const corteId of cortesAEliminar) {
        try {
          // Obtener el corte para encontrar sus parámetros
          const corte = await pb.collection('corte_conversion').getOne(corteId);
          // Eliminar los parámetros asociados
          if (corte && Array.isArray(corte.parametro)) {
            for (const parametroId of corte.parametro) {
              await pb.collection('corte_conversion_tabla').delete(parametroId);
            }
          }
          // Finalmente eliminar el corte
          await pb.collection('corte_conversion').delete(corteId);
        } catch (err) {
          ordenLogger.error("Error eliminando corte_conversion", err);
        }
      }
      
      // Actualizar la orden con los nuevos IDs de corte_conversion
      orderData.corte_conversion = corteConversionIds;
      
      // Ya no manejamos la impresión como una entidad separada
      // La impresión ahora está directamente relacionada con cada parámetro de corte_conversion_tabla
      ordenLogger.debug("Registros de impresión asociados a parámetros de corte_conversion_tabla");
      
      await ordenRepository.updateWithValidation(editId, orderData);

      setModalEdit(false);
      clearDirtyFields(); // Limpiar campos sucios después de actualizar exitosamente
      reloadOrdenes();
    } catch (err) {
      setModalError("Error al actualizar la orden y sus datos relacionados");
    }
    setSaving(false);
  };

  // Handlers para cerrar modal con confirmación si hay cambios (sistema isDirty)
  const handleCloseAdd = () => {
    if (hasUnsavedChanges) {
      setPendingCloseAction('add');
      setShowUnsavedChangesModal(true);
    } else {
      setModalAdd(false);
    }
  };

  const handleCloseEdit = () => {
    if (hasUnsavedChanges) {
      setPendingCloseAction('edit');
      setShowUnsavedChangesModal(true);
    } else {
      setModalEdit(false);
    }
  };

  // Handlers del modal de confirmación de cambios sin guardar
  const handleSaveChanges = async () => {
    try {
      if (pendingCloseAction === 'add') {
        await handleSave(); // Sin parámetro e
      } else if (pendingCloseAction === 'edit') {
        await handleUpdate(); // Sin parámetro e
      } else {
        throw new Error('Acción pendiente no válida');
      }
      
      setShowUnsavedChangesModal(false);
      setPendingCloseAction(null);
      clearDirtyFields();
    } catch (error) {
      // Mostrar error al usuario pero mantener el modal abierto
      setModalError(`Error al guardar cambios: ${error.message || 'Error desconocido'}`);
      ordenLogger.error("Error al guardar cambios", error);
    }
  };

  const handleDiscardChanges = () => {
    setShowUnsavedChangesModal(false);
    setPendingCloseAction(null);
    clearDirtyFields();
    
    if (pendingCloseAction === 'add') {
      setModalAdd(false);
    } else if (pendingCloseAction === 'edit') {
      setModalEdit(false);
    }
  };

  const handleCloseUnsavedModal = () => {
    setShowUnsavedChangesModal(false);
    setPendingCloseAction(null);
  };

  // --- Render ---
  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs maintitle="Dashboard" title="Producción" breadcrumbItem="Orden de Producción" />

          {/* Indicador de generación de PDF para móviles */}
          {generatingPdf && (
            <Alert
              color="info"
              isOpen={generatingPdf}
              fade={true}
              transition={{ timeout: 150 }}
              className="d-flex align-items-center mb-3"
            >
              <Spinner size="sm" className="me-2" />
              <span>Generando PDF... El archivo se descargará automáticamente.</span>
            </Alert>
          )}

          {/* Alerta del sistema para Iniciar Orden */}
          {alert.show && (
            <Alert
              id="system-alert"
              color={alert.type}
              isOpen={alert.show}
              toggle={() => setAlert({ show: false, type: '', title: '', message: '' })}
              fade={true}
              transition={{ timeout: 150 }}
              className="d-flex align-items-center justify-content-between mb-3"
            >
              <div>
                <strong>{alert.title}</strong> {alert.message}
              </div>
            </Alert>
          )}

          {showDeleteAlert && ordenToDelete && (
            <Alert
              color="warning"
              isOpen={showDeleteAlert}
              toggle={cancelRemove}
              fade={true}
              transition={{ timeout: 150 }}
              className="d-flex align-items-center justify-content-between"
            >
              <div>
                <strong>¿Está seguro?</strong> Esto eliminará la orden <b>{ordenToDelete.numero}</b>.
              </div>
              <div>
                <Button color="danger" size="sm" className="me-2" onClick={confirmRemove} disabled={deleting}>
                  {deleting ? <Spinner size="sm" /> : <i className="fas fa-eraser me-1"></i>} Confirmar
                </Button>
                <Button color="secondary" size="sm" onClick={cancelRemove} disabled={deleting}>
                  Cancelar
                </Button>
              </div>
            </Alert>
          )}

          <Row>
            <Col>
              <Card>
                <CardBody>
                  <CardTitle className="h4 d-flex justify-content-between align-items-center">
                    Orden de Producción
                    <div>
                      <Button color="primary" size="sm" onClick={handleAdd}>
                        <i className="fas fa-plus me-1"></i> Agregar
                      </Button>
                      <Button
                        color="secondary"
                        size="sm"
                        className="ms-2"
                        title="Recargar tabla"
                        onClick={reloadOrdenes}
                      >
                        <i className="fas fa-sync-alt"></i>
                      </Button>
                    </div>
                  </CardTitle>
                  <TableContainer
                    columns={columns}
                    data={ordenes}
                    isGlobalFilter={false}
                    isPagination={true}
                    isCustomPageSize={true}
                    pageIndex={pageIndex}
                    setPageIndex={setPageIndex}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    totalRecords={totalRecords}
                    pagination="pagination"
                    paginationWrapper='dataTables_paginate paging_simple_numbers'
                    tableClass="table table-bordered table-nowrap dt-responsive nowrap w-100"
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>

      {/* Modal para Agregar o Editar */}
      <Modal isOpen={modalAdd || modalEdit} toggle={modalEdit ? handleCloseEdit : handleCloseAdd} size="lg">
        <ModalHeader toggle={modalEdit ? handleCloseEdit : handleCloseAdd}>
          <div className="d-flex justify-content-between align-items-center w-100">
            <span>{modalEdit ? "Editar" : "Agregar"} Orden de Producción</span>
            {modalEdit && form.id && (
              <SolicitarMaterialButton 
                ordenId={form.id}
                disabled={!form.id}
                onMaterialRequested={() => {
                  // Optional: reload data or show notification
                  console.log("Material request completed");
                }}
              />
            )}
          </div>
        </ModalHeader>
        <Form id="orden-produccion-form" onSubmit={modalEdit ? handleUpdate : handleSave}>
          <ModalBody>
            {modalError && (
              <Alert
                color="danger"
                isOpen={!!modalError}
                toggle={() => setModalError("")}
                fade={true}
                transition={{ timeout: 150 }}
                className="d-flex align-items-center justify-content-between mb-3"
              >
                <div>
                  <strong>Error:</strong> {modalError}
                </div>
              </Alert>
            )}
            {modalEdit && form.id && (
              <div className="mb-3">
                <MaterialStatusIndicator ordenId={form.id} />
              </div>
            )}
                {/* Primer renglón: Número, Cliente y Asesor Comercial */}
                <Row>
                  {/* Usando el sistema de 12 columnas, 15% equivale aproximadamente a 2 columnas */}
                  <Col md={2}>
                    <FormGroup>
                      <Label for="numero">Número</Label>
                      <Input type="number" name="numero" id="numero" value={form.numero} onChange={handleChange} disabled />
                    </FormGroup>
                  </Col>
                  {/* Las 10 columnas restantes se dividen equitativamente entre Cliente y Asesor */}
                  <Col md={5}>
                    <FormGroup>
                      <Label for="cliente">Cliente</Label>
                      <Input type="select" name="cliente" id="cliente" value={form.cliente} onChange={handleChange} required>
                        <option value="">Seleccione un cliente</option>
                        {clientes.map(c => (
                          <option key={c.id} value={c.id}>{c.razon_social}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={5}>
                    <FormGroup>
                      <Label for="asesor_comercial">Asesor Comercial</Label>
                      <Input type="select" name="asesor_comercial" id="asesor_comercial" value={form.asesor_comercial} onChange={handleChange}>
                        <option value="">Seleccione un asesor</option>
                        {asesoresComerciales.map(asesor => (
                          <option key={asesor.id} value={asesor.id}>{asesor.name}</option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
                {/* Segundo renglón: Nombre Producto, Fecha Inicio, Fecha Entrega */}
                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="nombre_producto">Nombre del producto</Label>
                      <Input
                        type="text"
                        name="nombre_producto"
                        id="nombre_producto"
                        value={form.nombre_producto || ""}
                        onChange={handleChange}
                        placeholder="Ingrese el nombre del producto"
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="fecha_inicio">Fecha Inicio</Label>
                      <Input
                        type="datetime-local"
                        name="fecha_inicio"
                        id="fecha_inicio"
                        value={form.fecha_inicio}
                        onChange={handleChange}
                        required

                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="fecha_entrega">Fecha Entrega</Label>
                      <Input
                        type="datetime-local"
                        name="fecha_entrega"
                        id="fecha_entrega"
                        value={form.fecha_entrega}
                        onChange={handleChange}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {/* Nuevo renglón: Corte Facturación, Cantidad Solicitada, Checkbox Trabajo Interno y Solicitud */}
                <Row>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="corte_facturacion">Corte Facturación</Label>
                      <Input
                        type="datetime-local"
                        name="corte_facturacion"
                        id="corte_facturacion"
                        value={form.corte_facturacion}
                        onChange={handleChange}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={3}>
                    <FormGroup>
                      <Label for="cantidad_solicitada">Cantidad Solicitada</Label>
                      <Input
                        type="number"
                        name="cantidad_solicitada"
                        id="cantidad_solicitada"
                        value={form.cantidad_solicitada}
                        onChange={handleChange}
                        placeholder="Ingrese cantidad"
                      />
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="solicitud">Solicitud</Label>
                      <Input
                        type="select"
                        name="solicitud"
                        id="solicitud"
                        value={form.solicitud || ""}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seleccione tipo de solicitud</option>
                        {tiposSolicitud.map(tipo => (
                          <option key={tipo.id} value={tipo.id}>
                            {tipo.nombre}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={4}>
                    <FormGroup>
                      <Label for="numero_reproceso">N° Orden Reproceso</Label>
                      <Input
                        type="number"
                        name="numero_reproceso"
                        id="numero_reproceso"
                        value={form.numero_reproceso || ""}
                        onChange={handleChange}
                        disabled={!((tiposSolicitud.find(t => t.id === form.solicitud) || {}).campo_orden)}
                        placeholder="Ingrese número si aplica"
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {/* Nuevo renglón: Tipo Producto y Tipo de Trabajo */}
                <Row>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="tipo_producto">Tipo Producto</Label>
                      <Input
                        type="select"
                        name="tipo_producto"
                        id="tipo_producto"
                        value={form.tipo_producto || ""}
                        onChange={handleChange}
                        required
                      >
                        <option value="">Seleccione un tipo de producto</option>
                        {productos.map(producto => (
                          <option key={producto.id} value={producto.id}>
                            {producto.nombre}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <Label for="tipo_trabajo">Tipo de Trabajo</Label>
                      <Input
                        type="select"
                        name="tipo_trabajo"
                        id="tipo_trabajo"
                        value={form.trabajo_interno ? "interno" : "externo"}
                        onChange={(e) => {
                          const value = e.target.value;
                          handleChange({
                            target: {
                              name: "trabajo_interno",
                              value: value === "interno"
                            }
                          });
                        }}
                      >
                        <option value="">Seleccionar tipo de trabajo...</option>
                        <option value="interno">Interno</option>
                        <option value="externo">Externo</option>
                      </Input>
                    </FormGroup>
                  </Col>
                </Row>
                {/* Tercer renglón: Tipo Papel */}
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Planeacion
                        data={planeacionForm}
                        onChange={handlePlaneacionChange}
                        medidas={medidas}
                        tiposPinza={tiposPinza}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {/* Cuarto renglón: Facturación, Cantidad Solicitada, OC */}
                <Row>
                    <Col md={12}>
                    <FormGroup>
                      <CorteConversion
                        data={form.corte_conversion || []}
                        onChange={handleCorteConversionChange}
                        materiales={materiales}
                        medidas={medidas}
                        planeacionTamano={planeacionForm.planeacion_tamano || ""}
                        onValidationError={handleValidationError}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                {/* Quinto renglón: Nuevo, Re-impresión, Tipo Trabajo, Cabida Preprensa, Tintas */}
                <Row>
                  <Col md={12}>
                    <FormGroup>
                      <Impresion
                        data={impresionForm || []}
                        onChange={handleImpresionChange}
                        materiales={materiales}
                        medidas={medidas}
                        parametrosTinta={planeacionForm.parametros_tinta ? 
                          parametrosTinta.filter(p => planeacionForm.parametros_tinta.includes(p.id)) : []
                        }
                        onValidationError={handleValidationError}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                
                {/* Mini tabla de recubrimientos y tipos */}
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <Label className="mb-0">Parámetros y Tipos</Label>
                  <Button 
                    color="outline-secondary" 
                    size="sm" 
                    onClick={async (e) => {
                      e.target.disabled = true;
                      e.target.innerHTML = '<i class="fas fa-spinner fa-spin"></i> Actualizando...';
                      
                      try {
                        // Reload all parameter collections
                        const [brilloSubtipos, plastificadoSubtipos, pegueSubtipos, troqueladoSubtipos, acabadoSubtipos] = await Promise.all([
                          pb.collection("parametros_brillo_interno").getFullList({ filter: "estado=true", sort: "nombre" }),
                          pb.collection("parametros_plastificado_interno").getFullList({ filter: "estado=true", sort: "nombre" }),
                          pb.collection("parametros_pegue_interno").getFullList({ filter: "estado=true", sort: "nombre" }),
                          pb.collection("parametros_troquelado_interno").getFullList({ filter: "estado=true", sort: "nombre" }),
                          pb.collection("parametros_acabado_interno").getFullList({ filter: "estado=true", sort: "nombre" })
                        ]);
                        
                        // Update all state variables
                        setParametrosBrilloInterno(brilloSubtipos);
                        setParametrosPlastificadoInterno(plastificadoSubtipos);
                        setParametrosPegueInterno(pegueSubtipos);
                        setParametrosTroqueladoInterno(troqueladoSubtipos);
                        setParametrosAcabadoInterno(acabadoSubtipos);
                        
                        ordenLogger.debug("Parámetros actualizados manualmente", {
                          brillo: brilloSubtipos.length,
                          plastificado: plastificadoSubtipos.length,
                          pegue: pegueSubtipos.length,
                          troquelado: troqueladoSubtipos.length,
                          acabado: acabadoSubtipos.length
                        });
                      } catch (err) {
                        ordenLogger.error("Error actualizando parámetros", err);
                      } finally {
                        e.target.disabled = false;
                        e.target.innerHTML = '<i class="fas fa-sync-alt"></i> Actualizar parámetros';
                      }
                    }}
                  >
                    <i className="fas fa-sync-alt"></i> Actualizar parámetros
                  </Button>
                </div>
                
                <div className="table-responsive mb-3">
                  <table className="table table-bordered">
                    <thead>
                      <tr>
                        <th>Brillo</th>
                        <th>Plastificado</th>
                        <th>Pegue</th>
                        <th>Troquelado</th>
                        <th>Acabado</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>
                          {/* Columna de Brillo muestra el checklist de parametros_brillo_interno */}
                          {parametrosBrilloInterno.length > 0 ? 
                            parametrosBrilloInterno.map(brillo => (
                              <div key={brillo.id} className="form-check">
                                <Input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`brillo-${brillo.id}`}
                                  checked={Array.isArray(brilloForm.brillo_interno) && brilloForm.brillo_interno.includes(brillo.id)}
                                  onChange={e => handleBrilloInternoChange(brillo.id)}
                                />
                                <Label className="form-check-label" for={`brillo-${brillo.id}`}>
                                  {brillo.nombre}
                                </Label>
                              </div>
                            ))
                            : 
                            <p className="text-muted mb-0">No hay parámetros disponibles</p>
                          }
                        </td>
                        <td>
                          {/* Columna de Plastificado muestra el checklist de parametros_plastificado_interno */}
                          {parametrosPlastificadoInterno.length > 0 ? 
                            parametrosPlastificadoInterno.map(plastificado => (
                              <div key={plastificado.id} className="form-check">
                                <Input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`plastificado-${plastificado.id}`}
                                  checked={Array.isArray(plastificadoForm.plastificado_interno) && plastificadoForm.plastificado_interno.includes(plastificado.id)}
                                  onChange={e => handlePlastificadoInternoChange(plastificado.id)}
                                />
                                <Label className="form-check-label" for={`plastificado-${plastificado.id}`}>
                                  {plastificado.nombre}
                                </Label>
                              </div>
                            ))
                            : 
                            <p className="text-muted mb-0">No hay parámetros disponibles</p>
                          }
                        </td>
                        <td>
                          {/* Columna de Pegue muestra el checklist de parametros_pegue_interno */}
                          {parametrosPegueInterno.length > 0 ? 
                            parametrosPegueInterno.map(pegue => (
                              <div key={pegue.id} className="form-check">
                                <Input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`pegue-${pegue.id}`}
                                  checked={Array.isArray(pegueForm.pegue_interno) && pegueForm.pegue_interno.includes(pegue.id)}
                                  onChange={e => handlePegueInternoChange(pegue.id)}
                                />
                                <Label className="form-check-label" for={`pegue-${pegue.id}`}>
                                  {pegue.nombre}
                                </Label>
                              </div>
                            ))
                            : 
                            <p className="text-muted mb-0">No hay parámetros disponibles</p>
                          }
                        </td>
                        <td>
                          {/* Columna de Troquelado muestra el checklist de parametros_troquelado_interno */}
                          {parametrosTroqueladoInterno.length > 0 ? 
                            parametrosTroqueladoInterno.map(troquelado => (
                              <div key={troquelado.id} className="form-check">
                                <Input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`troquelado-${troquelado.id}`}
                                  checked={Array.isArray(troqueladoForm.troquelado_interno) && troqueladoForm.troquelado_interno.includes(troquelado.id)}
                                  onChange={e => handleTroqueladoInternoChange(troquelado.id)}
                                />
                                <Label className="form-check-label" for={`troquelado-${troquelado.id}`}>
                                  {troquelado.nombre}
                                </Label>
                              </div>
                            ))
                            : 
                            <p className="text-muted mb-0">No hay parámetros disponibles</p>
                          }
                        </td>
                        <td>
                          {/* Columna de Acabado muestra el checklist de parametros_acabado_interno */}
                          {parametrosAcabadoInterno.length > 0 ? 
                            parametrosAcabadoInterno.map(acabado => (
                              <div key={acabado.id} className="form-check">
                                <Input
                                  type="checkbox"
                                  className="form-check-input"
                                  id={`acabado-${acabado.id}`}
                                  checked={Array.isArray(acabadoForm.acabado_interno) && acabadoForm.acabado_interno.includes(acabado.id)}
                                  onChange={e => handleAcabadoInternoChange(acabado.id)}
                                />
                                <Label className="form-check-label" for={`acabado-${acabado.id}`}>
                                  {acabado.nombre}
                                </Label>
                              </div>
                            ))
                            : 
                            <p className="text-muted mb-0">No hay parámetros disponibles</p>
                          }
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div> 
                {/* Observaciones */}
                <FormGroup>
                  <Label>Observaciones</Label>
                  <Input
                    type="textarea"
                    name="observaciones"
                    id="observaciones"
                    value={form.observaciones}
                    onChange={handleChange}
                    rows={5}
                    className="mb-3"
                  />
                </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={modalEdit ? handleCloseEdit : handleCloseAdd}>
              Cancelar
            </Button>
            <Button color="primary" type="submit" disabled={saving}>
              {saving ? <Spinner size="sm" className="me-2" /> : null}
              {saving ? "Guardando..." : modalEdit ? "Actualizar" : "Guardar"}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Modal PDF Viewer */}
      <PDFViewerModal
        isOpen={isPdfModalOpen}
        toggle={() => setIsPdfModalOpen(false)}
        ordenId={pdfModalData.ordenId}
        filename={pdfModalData.filename}
      />

      {/* Modal de confirmación de cambios sin guardar */}
      <Modal
        isOpen={showUnsavedChangesModal}
        toggle={handleCloseUnsavedModal}
        size="md"
        centered
      >
        <ModalHeader className="bg-warning text-dark">
          <i className="fas fa-exclamation-triangle me-2"></i>
          Cambios sin guardar
        </ModalHeader>
        <ModalBody>
          <p>Tienes cambios sin guardar en el formulario. ¿Deseas salir sin guardar?</p>
          <p className="text-muted small">Acción pendiente: <strong>{pendingCloseAction || 'ninguna'}</strong></p>
          
          {/* Mostrar error si existe */}
          {modalError && (
            <Alert color="danger" className="mt-3">
              <strong>Error:</strong> {modalError}
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseUnsavedModal}>
            Cancelar
          </Button>
          <Button color="warning" onClick={handleDiscardChanges}>
            Salir sin guardar
          </Button>
          <Button color="primary" onClick={handleSaveChanges} disabled={saving}>
            {saving ? <Spinner size="sm" className="me-2" /> : null}
            {saving ? "Guardando..." : "Guardar cambios"}
          </Button>
        </ModalFooter>
      </Modal>

    </React.Fragment>
  );
};

export default OrdenProduccion;